<script setup>
/* eslint-disable no-console, no-lonely-if, no-nested-ternary, no-shadow, no-use-before-define, no-unused-vars, vue/no-bare-strings-in-template, vue/no-template-shadow, vue/prefer-true-attribute-shorthand, @intlify/vue-i18n/no-raw-text */
import { ref, watchEffect, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import conversationAPI from 'dashboard/api/inbox/conversation';
import ContactAPI from 'dashboard/api/contacts';
import {
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
  prepareNewMessagePayload,
  searchContacts,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import funnelAPI from '/plugins/kanban-board/src/api/funnel';
import offersAPI from '/plugins/kanban-board/src/api/offers';
import {
  buildAvailableOffers,
  isSelectedOfferId,
  selectDefaultOffer,
  syncOfferSelections,
} from '/plugins/kanban-board/src/utils/kanbanItemOffers';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  funnelId: {
    type: [String, Number],
    required: true,
  },
  stage: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    default: 0,
  },
  initialData: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  currentConversation: {
    type: Object,
    default: null,
  },
  initialDate: {
    type: Date,
    default: null,
  },
});

const emit = defineEmits(['saved', 'close']);

// Modificar a função toLocalISOString para retornar o formato exato
const toLocalISOString = (date, includeTime = true) => {
  if (!date) return null;
  try {
    // Ajuste para corrigir o problema do fuso horário
    let d = new Date(date);

    // Verificar se é apenas data (sem hora) para evitar problemas de fuso horário
    if (typeof date === 'string' && date.length === 10) {
      // Para datas sem hora, forçar hora 12:00 para evitar problemas de fuso
      const parts = date.split('-');
      // Importante: mês em JavaScript é baseado em zero (0-11)
      d = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    }

    // Obter ano, mês e dia de forma segura considerando fuso horário local
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    if (!includeTime) {
      return `${year}-${month}-${day}`;
    }

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    // Retornar no formato exato para datetime-local: YYYY-MM-DDThh:mm
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return null;
  }
};

// Função para extrair IDs dos agentes do formato completo
const extractAgentIds = assignedAgents => {
  if (!assignedAgents || !Array.isArray(assignedAgents)) return [];

  // Se é array de objetos completos (formato do JSON), extrair apenas os IDs
  if (
    assignedAgents.length > 0 &&
    typeof assignedAgents[0] === 'object' &&
    assignedAgents[0].id
  ) {
    return assignedAgents.map(agent => agent.id);
  }

  // Se já é array de IDs, retornar como está
  return assignedAgents;
};

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const activeTab = ref('general');
const currentUser = computed(() => store.getters.getCurrentUser);
const inboxesList = computed(() => store.getters['inboxes/getInboxes'] || []);
const formNoteDraft = ref('');

// Computed para tipos de campos traduzidos
const getFieldTypeLabel = type => {
  const typeMap = {
    string: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_TEXT'),
    number: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_NUMBER'),
    date: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_DATE'),
    boolean: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_BOOLEAN'),
  };
  return typeMap[type] || type;
};

const loading = ref(false);
const loadingAgents = ref(false);
const loadingFunnels = ref(false);
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Computed para obter os funis disponíveis
const availableFunnels = computed(() => {
  const funnels = store.getters['funnel/getFunnels'] || [];
  return funnels.filter(funnel => funnel.active);
});

// Ref para o funil selecionado no form
const selectedFunnelId = ref(null);

// Flag para evitar loops infinitos
const isUpdatingFunnel = ref(false);

// Watch para atualizar o store quando o funil for selecionado
watch(selectedFunnelId, async newFunnelId => {
  if (!newFunnelId || isUpdatingFunnel.value) return;

  const funnel = availableFunnels.value.find(f => f.id === newFunnelId);
  if (funnel && selectedFunnel.value?.id !== funnel.id) {
    isUpdatingFunnel.value = true;
    try {
      await store.dispatch('funnel/setSelectedFunnel', funnel);
      form.value.funnel_id = funnel.id;

      // Busca os stats das etapas do funil selecionado
      await store.dispatch('funnel/fetchStageStats', {
        funnelId: funnel.id,
        filterParams: {},
      });
    } catch (error) {
      console.error('Erro ao selecionar funil:', error);
    } finally {
      isUpdatingFunnel.value = false;
    }
  }
});

// Watch para atualizar o selectedFunnelId quando o selectedFunnel do store mudar
watch(
  selectedFunnel,
  newFunnel => {
    if (
      newFunnel?.id &&
      selectedFunnelId.value !== newFunnel.id &&
      !isUpdatingFunnel.value
    ) {
      selectedFunnelId.value = newFunnel.id;
    }
  },
  { immediate: true }
);

// Refs para controle do dropdown de agentes
const agentSearch = ref('');
const agentDropdownOpen = ref(false);
const agentSelectorRef = ref(null);
const filteredAgents = computed(() => {
  if (!agentSearch.value)
    return store.getters['agents/getAgents'].filter(
      a => !form.value.assigned_agents.some(sel => sel === a.id)
    );
  return store.getters['agents/getAgents'].filter(
    a =>
      !form.value.assigned_agents.some(sel => sel === a.id) &&
      a.name.toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

// Computed para obter as etapas do funil selecionado
const availableStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];

  return Object.entries(selectedFunnel.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
      itemCount:
        stage.item_count ??
        stage.items_count ??
        stage.total_items ??
        stage.count ??
        0,
    }))
    .sort((a, b) => a.position - b.position);
});

const getFunnelOptionLabel = funnel => {
  if (!funnel) {
    return '';
  }

  return `${funnel.name} (#${funnel.id})`;
};

const getStageOptionLabel = stage => {
  if (!stage) {
    return '';
  }

  return `${stage.name} (${stage.itemCount})`;
};

// Refs para controle da conversa
const showConversationInput = ref(
  !!props.initialData?.item_details?.conversation_id
);
const conversations = ref([]);
const loadingConversations = ref(false);
const showNewConversationInput = ref(false);
const newConversationContactSearch = ref('');
const newConversationContacts = ref([]);
const newConversationLoadingContacts = ref(false);
const newConversationSelectedContact = ref(null);
const newConversationInboxes = ref([]);
const newConversationLoadingInboxes = ref(false);
const newConversationSelectedInbox = ref(null);
const newConversationMessage = ref('');
const newConversationCreating = ref(false);

// Dados para automação de status
const buildDefaultStatusAutomations = () => [
  {
    id: 'status_won_move_stage',
    enabled: false,
    trigger_type: 'status_change',
    trigger_value: 'won',
    action: 'move_to_stage',
    action_config: { stage: '' },
  },
  {
    id: 'status_lost_assign_agent',
    enabled: false,
    trigger_type: 'status_change',
    trigger_value: 'lost',
    action: 'assign_agent',
    action_config: { agent_id: '' },
  },
  {
    id: 'status_won_create_note',
    enabled: false,
    trigger_type: 'status_change',
    trigger_value: 'won',
    action: 'create_note',
    action_config: { note_text: 'Cliente fechou negócio com sucesso!' },
  },
];

const statusAutomations = ref(buildDefaultStatusAutomations());

// Refs para controle das ofertas
const availableOffers = ref([]);
const responseOffersCache = ref([]);
const loadingOffers = ref(false);
const showOffersInput = ref(!!props.initialData?.item_details?.offers?.length);
const showManualOfferForm = ref(false);
const manualOffer = ref({
  description: '',
  value: null,
  currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
});
// Busca e filtro de conversas
const conversationSearch = ref('');
const conversationDropdownOpen = ref(false);
const conversationSelectorRef = ref(null);
const conversationDropdownStyle = ref({
  top: '0px',
  left: '0px',
  width: '100%',
});
const getConversationDisplayName = conversation => {
  const sender = conversation?.meta?.sender || conversation?.contact || {};
  return (
    sender.name ||
    sender.email ||
    t('KANBAN.FORM.CONVERSATION.RELATED_FALLBACK', {
      id: conversation?.id || '?',
    })
  );
};
const findConversationById = conversationId => {
  return (
    conversations.value.find(
      conversation => String(conversation.id) === String(conversationId)
    ) || null
  );
};
const mergeConversationOptions = (...conversationGroups) => {
  const mergedConversations = new Map();

  conversationGroups.flat().forEach(conversation => {
    if (conversation?.id == null) return;
    mergedConversations.set(String(conversation.id), conversation);
  });

  conversations.value = Array.from(mergedConversations.values());
};
const getContextConversation = conversationId => {
  if (String(props.currentConversation?.id) === String(conversationId)) {
    return props.currentConversation;
  }

  if (String(props.initialData?.conversation?.id) === String(conversationId)) {
    return props.initialData.conversation;
  }

  return null;
};
const getConversationSummary = conversation => {
  return `#${conversation.id} - ${getConversationDisplayName(conversation)}`;
};
const filteredConversations = computed(() => {
  if (!conversationSearch.value) return conversations.value;
  return conversations.value.filter(conversation => {
    const text = getConversationSummary(conversation).toLowerCase();
    return text.includes(conversationSearch.value.toLowerCase());
  });
});

const normalizeContactRecord = contact => {
  if (!contact) return null;

  return {
    ...contact,
    phoneNumber: contact.phoneNumber || contact.phone_number || '',
    thumbnail: contact.thumbnail || contact.thumbnail_url || '',
  };
};

const getContactSubtitle = contact => {
  return (
    contact?.email ||
    contact?.phoneNumber ||
    contact?.phone_number ||
    t('KANBAN.FORM.CONVERSATION.NEW.NO_CONTACT_DETAIL')
  );
};

const formatNewContactInput = value => {
  const input = value.trim();
  if (!input || input.includes('@')) return input;

  const phone = input.replace(/[^\d+]/g, '');
  if (!phone) return input;
  return phone.startsWith('+') ? phone : `+${phone}`;
};

const resetNewConversationState = () => {
  newConversationContactSearch.value = '';
  newConversationContacts.value = [];
  newConversationSelectedContact.value = null;
  newConversationInboxes.value = [];
  newConversationSelectedInbox.value = null;
  newConversationMessage.value = '';
  newConversationLoadingContacts.value = false;
  newConversationLoadingInboxes.value = false;
  newConversationCreating.value = false;
};

const mergeContactableInboxes = inboxes => {
  return mergeInboxDetails(inboxes, inboxesList.value);
};

const loadInitialNewConversationContacts = async () => {
  try {
    newConversationLoadingContacts.value = true;
    const response = await ContactAPI.get(1);
    const contactsPayload = response.data?.payload || [];
    newConversationContacts.value = contactsPayload
      .map(normalizeContactRecord)
      .filter(contact => contact?.id);
  } catch (error) {
    newConversationContacts.value = [];
  } finally {
    newConversationLoadingContacts.value = false;
  }
};

const searchNewConversationContacts = async () => {
  const query = newConversationContactSearch.value.trim();
  if (!query) {
    await loadInitialNewConversationContacts();
    return;
  }

  try {
    newConversationLoadingContacts.value = true;
    newConversationContacts.value = await searchContacts({
      keys: ['email', 'phone_number', 'name'],
      query,
    });
  } catch (error) {
    newConversationContacts.value = [];
    emitter.emit('newToastMessage', {
      message: t('KANBAN.FORM.CONVERSATION.NEW.SEARCH_ERROR'),
      type: 'error',
    });
  } finally {
    newConversationLoadingContacts.value = false;
  }
};

const loadNewConversationInboxes = async contact => {
  if (!contact?.id) return;

  try {
    newConversationLoadingInboxes.value = true;
    const inboxes = await fetchContactableInboxes(contact.id);
    newConversationInboxes.value = mergeContactableInboxes(inboxes);
    newConversationSelectedInbox.value =
      newConversationInboxes.value[0] || null;
  } catch (error) {
    newConversationInboxes.value = [];
    newConversationSelectedInbox.value = null;
    emitter.emit('newToastMessage', {
      message: t('KANBAN.FORM.CONVERSATION.NEW.INBOX_ERROR'),
      type: 'error',
    });
  } finally {
    newConversationLoadingInboxes.value = false;
  }
};

const selectNewConversationContact = async contact => {
  newConversationSelectedContact.value = normalizeContactRecord(contact);
  await loadNewConversationInboxes(newConversationSelectedContact.value);
};

const createContactFromNewConversationInput = async () => {
  const input = formatNewContactInput(newConversationContactSearch.value);
  if (!input) {
    throw new Error(t('KANBAN.FORM.CONVERSATION.NEW.CONTACT_REQUIRED'));
  }

  try {
    const contact = await createNewContact(input);
    newConversationSelectedContact.value = normalizeContactRecord(contact);
    await loadNewConversationInboxes(newConversationSelectedContact.value);
    return newConversationSelectedContact.value;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.FORM.CONVERSATION.NEW.CONTACT_CREATE_ERROR'),
      type: 'error',
    });
    throw error;
  }
};

// Atualizar a função para verificar se tem agendamento
const hasScheduling = computed(() => {
  return !!(
    props.initialData?.item_details?.scheduled_at ||
    props.initialData?.item_details?.deadline_at ||
    props.initialDate
  );
});

// Atualizar a ref showScheduling para usar o computed
const showScheduling = ref(hasScheduling.value);

// Forçar showScheduling a ser true se houver agendamento
// Isso garante que o checkbox esteja marcado
if (hasScheduling.value) {
  showScheduling.value = true;
}

// Atualizar o schedulingType para usar o tipo correto do item
const schedulingType = ref(
  props.initialData?.item_details?.scheduling_type ||
    (props.initialData?.item_details?.scheduled_at ? 'schedule' : 'deadline')
);

// Atualizar o form.value para incluir as ofertas e datas
const form = ref({
  title: props.initialData?.title || '',
  description:
    props.initialData?.item_details?.description ||
    props.initialData?.description ||
    '',
  funnel_id: props.funnelId,
  funnel_stage: props.stage,
  position: props.position,
  assigned_agents: extractAgentIds(props.initialData?.assigned_agents) || [],
  item_details: {
    title: props.initialData?.title || '',
    description:
      props.initialData?.item_details?.description ||
      props.initialData?.description ||
      '',
    value: null,
    notes: Array.isArray(props.initialData?.item_details?.notes)
      ? JSON.parse(JSON.stringify(props.initialData.item_details.notes))
      : [],
    offers: [],
    priority:
      props.initialData?.item_details?.priority ||
      props.initialData?.priority ||
      'none',
    conversation_id: props.initialData?.item_details?.conversation_id || null,
    scheduling_type: schedulingType.value,
    scheduled_at: null,
    deadline_at: null,
    forgotten_after_hours:
      props.initialData?.item_details?.forgotten_after_hours || null,
    expected_close_at:
      toLocalISOString(
        props.initialData?.item_details?.expected_close_at,
        false
      ) || null,
  },
  custom_attributes: props.initialData?.item_details?.custom_attributes || [],
});

const selectedStageBorderStyle = computed(() => {
  const stageId = form.value.funnel_stage;
  const color = selectedFunnel.value?.stages?.[stageId]?.color;

  if (!color) {
    return {};
  }

  const backgroundColor = color.length === 7 ? `${color}18` : color;

  return {
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: color,
    backgroundColor,
  };
});

const getRelatedChecklistItems = () => {
  if (Array.isArray(props.initialData?.checklist)) {
    return props.initialData.checklist;
  }

  if (Array.isArray(props.initialData?.item_details?.checklist)) {
    return props.initialData.item_details.checklist;
  }

  return [];
};
const selectedConversation = computed(() => {
  return (
    findConversationById(form.value.item_details.conversation_id) ||
    getContextConversation(form.value.item_details.conversation_id) ||
    null
  );
});
const relatedConversations = computed(() => {
  const relatedSources = new Map();
  const addRelatedConversation = (conversationId, source) => {
    if (!conversationId) return;

    const key = String(conversationId);
    if (!relatedSources.has(key)) {
      relatedSources.set(key, new Set());
    }

    relatedSources.get(key).add(source);
  };

  addRelatedConversation(
    form.value.item_details.conversation_id ||
      form.value.conversation_display_id ||
      props.currentConversation?.id,
    'primary'
  );

  const relatedNotes = Array.isArray(props.initialData?.item_details?.notes)
    ? props.initialData.item_details.notes
    : [];
  relatedNotes.forEach(note => {
    addRelatedConversation(note?.linked_conversation_id, 'note');
  });

  getRelatedChecklistItems().forEach(item => {
    addRelatedConversation(item?.linked_conversation_id, 'checklist');
  });

  return Array.from(relatedSources.entries())
    .map(([conversationId, sources]) => {
      const conversation =
        findConversationById(conversationId) ||
        getContextConversation(conversationId);

      return {
        id: conversation?.id || conversationId,
        label: getConversationDisplayName(conversation),
        sources: Array.from(sources),
        isPrimary:
          String(form.value.item_details.conversation_id) ===
          String(conversationId),
      };
    })
    .sort((left, right) =>
      String(left.id).localeCompare(String(right.id), undefined, {
        numeric: true,
      })
    );
});

const createNewConversationFromForm = async () => {
  if (newConversationCreating.value) return null;

  newConversationCreating.value = true;
  try {
    const contact =
      newConversationSelectedContact.value ||
      (await createContactFromNewConversationInput());

    const inbox =
      newConversationSelectedInbox.value || newConversationInboxes.value[0];

    if (!contact?.id) {
      throw new Error(t('KANBAN.FORM.CONVERSATION.NEW.CONTACT_REQUIRED'));
    }

    if (!inbox?.id) {
      throw new Error(t('KANBAN.FORM.CONVERSATION.NEW.INBOX_REQUIRED'));
    }

    const message =
      newConversationMessage.value.trim() ||
      t('KANBAN.FORM.CONVERSATION.NEW.DEFAULT_MESSAGE', {
        title: form.value.title || props.initialData?.title || '',
      });

    const payload = prepareNewMessagePayload({
      targetInbox: inbox,
      selectedContact: contact,
      message,
      subject: null,
      ccEmails: null,
      bccEmails: null,
      currentUser: currentUser.value,
      attachedFiles: [],
      directUploadsEnabled: false,
    });

    const isFromWhatsApp =
      inbox.channelType === 'Channel::Whatsapp' ||
      (inbox.channelType === 'Channel::TwilioSms' &&
        inbox.medium === 'whatsapp');

    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });
    const conversationId = data.display_id || data.id;

    form.value.item_details.conversation_id = conversationId;
    form.value.conversation_display_id = conversationId;
    showConversationInput.value = true;
    mergeConversationOptions(data);

    emitter.emit('newToastMessage', {
      message: t('KANBAN.FORM.CONVERSATION.NEW.CREATED_SUCCESS'),
      type: 'success',
    });

    return data;
  } finally {
    newConversationCreating.value = false;
  }
};

const navigateToCreatedConversation = conversation => {
  if (!conversation) return;

  const accountId = conversation.account_id || currentUser.value?.account_id;
  const conversationId = conversation.display_id || conversation.id;
  if (!accountId || !conversationId) return;

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId,
      conversation_id: conversationId,
    },
  });
};

const formNotes = computed(() =>
  Array.isArray(form.value.item_details?.notes)
    ? form.value.item_details.notes
    : []
);

const formatFormNoteDate = date => {
  if (!date) {
    return '';
  }

  try {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const addFormNote = () => {
  const noteText = formNoteDraft.value.trim();

  if (!noteText) {
    return;
  }

  const createdAt = new Date().toISOString();
  const notes = Array.isArray(form.value.item_details.notes)
    ? [...form.value.item_details.notes]
    : [];

  notes.push({
    id: `form-note-${Date.now()}`,
    text: noteText,
    created_at: createdAt,
    updated_at: createdAt,
    attachments: [],
    author: currentUser.value?.name || t('KANBAN.SYSTEM'),
    author_id: currentUser.value?.id || null,
    author_avatar: currentUser.value?.avatar_url || null,
    created_in_form: true,
  });

  form.value.item_details.notes = notes;
  formNoteDraft.value = '';
};

const removePendingFormNote = noteId => {
  form.value.item_details.notes = formNotes.value.filter(
    note => !(note.created_in_form && note.id === noteId)
  );
};

const getConversationSourceLabel = source => {
  const sourceLabels = {
    primary: t('KANBAN.FORM.CONVERSATION.RELATED_SOURCE_PRIMARY'),
    note: t('KANBAN.FORM.CONVERSATION.RELATED_SOURCE_NOTE'),
    checklist: t('KANBAN.FORM.CONVERSATION.RELATED_SOURCE_CHECKLIST'),
  };

  return sourceLabels[source] || source;
};

// Adicione estas refs
const customAttributes = ref(
  Array.isArray(props.initialData?.item_details?.custom_attributes)
    ? props.initialData.item_details.custom_attributes.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }))
    : Array.isArray(props.initialData?.custom_attributes)
      ? props.initialData.custom_attributes.map(attr => ({
          name: attr.name || '',
          type: attr.type || '',
        }))
      : []
);

// Campos globais do funil
const globalCustomAttributes = ref([]);
const globalCustomAttributesValues = ref({});

// Adicionar um watch para atualizar customAttributes ao trocar de item para edição
watch(
  () => [
    props.initialData?.item_details?.custom_attributes,
    props.initialData?.custom_attributes,
  ],
  ([newItemDetailsAttrs, newRootAttrs]) => {
    if (Array.isArray(newItemDetailsAttrs)) {
      customAttributes.value = newItemDetailsAttrs.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }));
    } else if (Array.isArray(newRootAttrs)) {
      customAttributes.value = newRootAttrs.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }));
    } else {
      customAttributes.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Modifique o onMounted para remover a busca de agentes
onMounted(async () => {
  store.dispatch('inboxes/get');

  // Carregar agentes do store
  loadingAgents.value = true;
  try {
    await store.dispatch('agents/get');

    // Selecionar automaticamente o usuário atual ao criar novo item
    if (!props.isEditing && form.value.assigned_agents.length === 0) {
      const currentUser = store.getters.getCurrentUser;
      if (currentUser?.id) {
        form.value.assigned_agents.push(currentUser.id);
      }
    }
  } catch (error) {
    // Error handling
  } finally {
    loadingAgents.value = false;
  }

  // Carregar funis do store
  loadingFunnels.value = true;
  try {
    await store.dispatch('funnel/fetch');

    // Inicializar o selectedFunnelId
    if (selectedFunnel.value?.id) {
      selectedFunnelId.value = selectedFunnel.value.id;
    } else if (availableFunnels.value.length > 0) {
      // Se não houver funil selecionado e existirem funis, seleciona o primeiro
      selectedFunnelId.value = availableFunnels.value[0].id;
    }

    // Se já há um funil selecionado, busca os stats dele
    if (selectedFunnel.value?.id) {
      await store.dispatch('funnel/fetchStageStats', {
        funnelId: selectedFunnel.value.id,
        filterParams: {},
      });
    }
  } catch (error) {
    console.error('Erro ao carregar funis:', error);
  } finally {
    loadingFunnels.value = false;
  }

  // Se tiver conversa atual ou estiver editando, buscar conversas
  if (
    props.currentConversation ||
    (props.isEditing && props.initialData?.item_details?.conversation_id)
  ) {
    showConversationInput.value = true;
    fetchConversations();
  }

  // Buscar ofertas disponíveis
  fetchOffers();

  // Carregar campos globais do funil se estiver editando
  if (props.isEditing && props.initialData?.funnel_id) {
    loadGlobalAttributesForFunnel(props.initialData.funnel_id);
  }

  // Processar ofertas existentes ao carregar o formulário em modo de edição
  if (props.isEditing && props.initialData?.item_details?.offers?.length) {
    form.value.item_details.offers = [...props.initialData.item_details.offers];
    syncFormOffers();
  }

  // Sincronizar valores de agendamento
  if (hasScheduling.value) {
    showScheduling.value = true;

    if (props.initialData?.item_details?.scheduled_at) {
      schedulingType.value = 'schedule';
      form.value.item_details.scheduled_at = toLocalISOString(
        props.initialData.item_details.scheduled_at
      );
    } else if (props.initialData?.item_details?.deadline_at) {
      schedulingType.value = 'deadline';
      form.value.item_details.deadline_at = toLocalISOString(
        props.initialData.item_details.deadline_at,
        false // Apenas data para prazo
      );
    } else if (props.initialDate) {
      // Se houver uma data inicial, configure como prazo
      schedulingType.value = 'deadline';
      form.value.item_details.deadline_at = toLocalISOString(
        props.initialDate,
        false // Apenas data para prazo
      );
    }
  }
});

// Adicionar watch para o campo showScheduling
watch(showScheduling, newValue => {
  if (!newValue) {
    // Se o checkbox foi desmarcado, limpe os campos de agendamento
    form.value.item_details.scheduled_at = null;
    form.value.item_details.deadline_at = null;
  } else {
    // Se foi marcado mas não há tipo definido, defina um padrão
    if (!form.value.item_details.scheduling_type) {
      schedulingType.value = 'deadline';
      form.value.item_details.scheduling_type = 'deadline';
    }
  }
});

// Atualizar o watch do schedulingType
watch(schedulingType, (newType, oldType) => {
  if (newType === oldType) return;

  form.value.item_details.scheduling_type = newType;

  if (newType === 'deadline') {
    // Se mudar para deadline, preserva a data existente ou converte do scheduled_at
    if (
      !form.value.item_details.deadline_at &&
      form.value.item_details.scheduled_at
    ) {
      // Converter para o formato de date
      form.value.item_details.deadline_at = toLocalISOString(
        form.value.item_details.scheduled_at,
        false // Sem hora
      );
    }
    form.value.item_details.scheduled_at = null;
  } else {
    // Se mudar para schedule, preserva a data existente ou converte do deadline_at
    if (
      !form.value.item_details.scheduled_at &&
      form.value.item_details.deadline_at
    ) {
      // Converter para o formato de datetime-local
      const deadlineDate = new Date(form.value.item_details.deadline_at);
      // Define hora como meio-dia por padrão
      deadlineDate.setHours(12, 0, 0, 0);
      form.value.item_details.scheduled_at = toLocalISOString(deadlineDate);
    }
    form.value.item_details.deadline_at = null;
  }
});

// Função para buscar conversas
const fetchConversations = async () => {
  try {
    loadingConversations.value = true;
    const initialConversation = props.initialData?.conversation;

    if (props.currentConversation) {
      mergeConversationOptions(props.currentConversation, initialConversation);
    } else {
      const response = await conversationAPI.get({});
      mergeConversationOptions(
        initialConversation,
        response.data.data.payload || []
      );
    }
  } catch (error) {
    mergeConversationOptions(
      props.currentConversation,
      props.initialData?.conversation
    );
  } finally {
    loadingConversations.value = false;
  }
};

// Função para buscar ofertas disponíveis
const refreshAvailableOffers = () => {
  availableOffers.value = buildAvailableOffers(
    responseOffersCache.value,
    form.value.item_details.offers
  );
};

const syncFormOffers = () => {
  const { offers, value, currency } = syncOfferSelections(
    form.value.item_details.offers
  );

  form.value.item_details.offers = offers;
  form.value.item_details.value = value;
  form.value.item_details.currency = currency;
};

const fetchOffers = async () => {
  try {
    loadingOffers.value = true;
    const response = await offersAPI.getOffers();
    responseOffersCache.value = response.data || [];
    refreshAvailableOffers();
  } catch (error) {
    console.error('Erro ao carregar ofertas:', error);
    responseOffersCache.value = [];
    availableOffers.value = [];
  } finally {
    loadingOffers.value = false;
  }
};

// Função para carregar atributos globais de um funil específico
const loadGlobalAttributesForFunnel = async funnelId => {
  try {
    const response = await funnelAPI.get();
    const funnels = response.data || [];
    const selectedFunnelData = funnels.find(f => f.id === funnelId);

    // Inicializar campos globais do funil
    if (selectedFunnelData?.global_custom_attributes?.length > 0) {
      globalCustomAttributes.value = [
        ...selectedFunnelData.global_custom_attributes,
      ];

      // Buscar valores existentes dos custom_attributes (que é um array)
      const existingCustomAttrs =
        props.initialData?.item_details?.custom_attributes || [];

      // Inicializar valores dos campos globais com dados existentes
      const existingGlobalValues = {};
      selectedFunnelData.global_custom_attributes.forEach(attr => {
        // Buscar o valor atual deste campo no array de custom_attributes
        const existingAttr = Array.isArray(existingCustomAttrs)
          ? existingCustomAttrs.find(ca => ca.name === attr.name)
          : null;

        if (existingAttr) {
          // Usar o valor existente
          existingGlobalValues[attr.name] = existingAttr.value;
        } else if (attr.is_list) {
          // Para campos de lista novos, inicializar como array com um item vazio
          existingGlobalValues[attr.name] = [''];
        } else {
          // Para campos simples novos, inicializar vazio
          existingGlobalValues[attr.name] = '';
        }
      });
      globalCustomAttributesValues.value = existingGlobalValues;
    } else {
      globalCustomAttributes.value = [];
      globalCustomAttributesValues.value = {};
    }
  } catch (error) {
    console.error('Erro ao carregar atributos globais do funil:', error);
    globalCustomAttributes.value = [];
    globalCustomAttributesValues.value = {};
  }
};

watch(showConversationInput, newValue => {
  if (!newValue) {
    conversationDropdownOpen.value = false;
    form.value.item_details.conversation_id = null;
  } else {
    showNewConversationInput.value = false;
    fetchConversations();
  }
});

watch(showNewConversationInput, newValue => {
  if (newValue) {
    showConversationInput.value = false;
    conversationDropdownOpen.value = false;
    form.value.item_details.conversation_id = null;
    form.value.conversation_display_id = null;
    if (!newConversationContacts.value.length) {
      loadInitialNewConversationContacts();
    }
  } else {
    resetNewConversationState();
  }
});

watch(showOffersInput, newValue => {
  if (!newValue) {
    showManualOfferForm.value = false;
    form.value.item_details.offers = [];
    form.value.item_details.value = null;
    form.value.item_details.currency = null;
  }
});

// Funções para gerenciar ofertas
const isOfferSelected = offerId => {
  return isSelectedOfferId(form.value.item_details.offers, offerId);
};

const toggleOfferSelection = offer => {
  const offerIndex = form.value.item_details.offers.findIndex(o => {
    // Verificar correspondência por ID ou offer_id
    if (o.id) return o.id === offer.id;
    if (o.offer_id) return o.offer_id === offer.id;
    return false;
  });

  if (offerIndex !== -1) {
    // Remover oferta
    form.value.item_details.offers.splice(offerIndex, 1);
  } else {
    // Adicionar oferta
    form.value.item_details.offers.push({
      id: offer.id, // Preservar o ID da oferta
      description: offer.name,
      value: offer.discount_percentage,
      currency: {
        symbol:
          offer.currency === 'BRL'
            ? 'R$'
            : offer.currency === 'USD'
              ? '$'
              : '€',
        code: offer.currency,
        locale:
          offer.currency === 'BRL'
            ? 'pt-BR'
            : offer.currency === 'USD'
              ? 'en-US'
              : 'de-DE',
      },
    });
  }

  refreshAvailableOffers();
  syncFormOffers();
};

const addManualOffer = () => {
  if (!manualOffer.value.description || !manualOffer.value.value) {
    emitter.emit('newToastMessage', {
      message: 'Preencha descrição e valor da oferta',
      type: 'error',
    });
    return;
  }

  form.value.item_details.offers.push({
    manual: true, // Marcar como oferta manual
    description: manualOffer.value.description,
    value: parseFloat(manualOffer.value.value),
    currency: manualOffer.value.currency,
  });

  syncFormOffers();

  // Resetar o formulário
  manualOffer.value = {
    description: '',
    value: null,
    currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
  };
  showManualOfferForm.value = false;

  emitter.emit('newToastMessage', {
    message: 'Oferta adicionada com sucesso',
    type: 'success',
  });
};

const cancelManualOffer = () => {
  manualOffer.value = {
    description: '',
    value: null,
    currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
  };
  showManualOfferForm.value = false;
};

const removeOffer = index => {
  form.value.item_details.offers.splice(index, 1);
  refreshAvailableOffers();
  syncFormOffers();
};

const setDefaultOffer = index => {
  const selectedOffer = form.value.item_details.offers[index];

  if (!selectedOffer) {
    return;
  }

  form.value.item_details.offers = selectDefaultOffer(
    form.value.item_details.offers,
    selectedOffer
  );
  syncFormOffers();
};

// Atualiza o form quando o funil ou etapa mudar
watchEffect(() => {
  form.value = {
    ...form.value,
    funnel_id: selectedFunnel.value?.id || props.funnelId,
    funnel_stage: form.value.funnel_stage || props.stage,
    position: props.position,
    assigned_agents:
      form.value.assigned_agents ||
      extractAgentIds(props.initialData?.assigned_agents) ||
      [],
    title:
      form.value.title ||
      props.initialData?.title ||
      props.initialData?.item_details?.title ||
      '',
    item_details: {
      ...form.value.item_details,
      title:
        form.value.title ||
        props.initialData?.title ||
        props.initialData?.item_details?.title ||
        '',
      description: form.value.description,
      priority:
        form.value.item_details.priority ||
        props.initialData?.item_details?.priority ||
        props.initialData?.priority ||
        'none',
      value:
        form.value.item_details.value || props.initialData?.item_details?.value,
      currency:
        form.value.item_details.currency ||
        props.initialData?.item_details?.currency,
      scheduled_at:
        form.value.item_details.scheduled_at ||
        props.initialData?.item_details?.scheduled_at,
      deadline_at:
        form.value.item_details.deadline_at ||
        props.initialData?.item_details?.deadline_at,
      forgotten_after_hours:
        form.value.item_details.forgotten_after_hours ||
        props.initialData?.item_details?.forgotten_after_hours,
      expected_close_at:
        form.value.item_details.expected_close_at ||
        toLocalISOString(
          props.initialData?.item_details?.expected_close_at,
          false
        ),
      notes:
        form.value.item_details.notes ||
        props.initialData?.item_details?.notes ||
        [],
    },
  };
});

// Adicionar watch para sincronizar o título em tempo real
watch(
  () => form.value.title,
  newTitle => {
    if (form.value.item_details) {
      form.value.item_details.title = newTitle;
    }
  }
);

// Adicionar watch para sincronizar a descrição em tempo real
watch(
  () => form.value.description,
  newDescription => {
    if (form.value.item_details) {
      form.value.item_details.description = newDescription;
    }
  }
);

// Adicionar watch para sincronizar assigned_agents quando initialData mudar
watch(
  () => props.initialData?.assigned_agents,
  newAssignedAgents => {
    if (Array.isArray(newAssignedAgents)) {
      form.value.assigned_agents = extractAgentIds(newAssignedAgents);
    }
  },
  { immediate: true, deep: true }
);

// Watch para sincronizar ofertas ao abrir para edição
watch(
  () => props.initialData?.item_details?.offers,
  newOffers => {
    if (props.isEditing && newOffers?.length) {
      if (form.value.item_details.offers.length === 0) {
        form.value.item_details.offers = [...newOffers];
      }
      // Marcar o checkbox se houver ofertas
      if (!showOffersInput.value) {
        showOffersInput.value = true;
      }
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.initialData?.item_details?.notes,
  newNotes => {
    if (props.isEditing && Array.isArray(newNotes)) {
      form.value.item_details.notes = JSON.parse(JSON.stringify(newNotes));
    }
  },
  { immediate: true, deep: true }
);

// Watch para carregar automações existentes ao abrir para edição
watch(
  () => props.initialData?.item_details?.automations,
  newAutomations => {
    // Só executar se statusAutomations estiver inicializado
    if (statusAutomations.value && props.isEditing && newAutomations?.length) {
      // Atualizar automações existentes com dados salvos
      statusAutomations.value = newAutomations.map(auto => ({
        id: auto.id || `automation_${Date.now()}_${Math.random()}`,
        enabled: auto.enabled !== false,
        trigger_type: auto.trigger_type || 'status_change',
        trigger_value: auto.trigger_value || '',
        action: auto.action || '',
        action_config: auto.action_config || {},
      }));

      // Se não houver automações salvas, manter as padrão
      if (statusAutomations.value.length === 0) {
        statusAutomations.value = buildDefaultStatusAutomations();
      }
    }
  },
  { immediate: true, deep: true }
);

// Adicionar watch para atualizar valores globais quando o funil mudar
watch(
  () => selectedFunnel.value?.id,
  async newFunnelId => {
    // Só atualizar se não estiver editando (quando editando, usamos o funnel_id do item)
    if (
      newFunnelId &&
      activeTab.value === 'custom_fields' &&
      !props.isEditing
    ) {
      try {
        const response = await funnelAPI.get();
        const funnels = response.data || [];
        const selectedFunnelData = funnels.find(f => f.id === newFunnelId);

        // Atualizar campos globais quando o funil mudar
        if (selectedFunnelData?.global_custom_attributes?.length > 0) {
          globalCustomAttributes.value = [
            ...selectedFunnelData.global_custom_attributes,
          ];

          // Preservar valores existentes ou inicializar vazios
          const existingGlobalValues = {
            ...globalCustomAttributesValues.value,
          };
          selectedFunnelData.global_custom_attributes.forEach(attr => {
            if (!(attr.name in existingGlobalValues)) {
              if (attr.is_list) {
                existingGlobalValues[attr.name] = [''];
              } else {
                existingGlobalValues[attr.name] = '';
              }
            }
          });
          globalCustomAttributesValues.value = existingGlobalValues;
        } else {
          globalCustomAttributes.value = [];
          globalCustomAttributesValues.value = {};
        }
      } catch (error) {
        console.error('Erro ao atualizar campos globais:', error);
      }
    }
  },
  { immediate: true }
);

// Modificar a função validateForm para incluir a validação de data
const validateForm = () => {
  const errors = {};

  if (!form.value.title?.trim()) {
    errors.title = t('KANBAN.ERRORS.TITLE_REQUIRED');
  }

  if (!form.value.funnel_id) {
    errors.funnel_id = t('KANBAN.ERRORS.FUNNEL_REQUIRED');
  }

  if (!form.value.funnel_stage) {
    errors.funnel_stage = t('KANBAN.ERRORS.STAGE_REQUIRED');
  }

  // Removidas todas as validações de data/prazo

  if (Object.keys(errors).length > 0) {
    emitter.emit('newToastMessage', {
      message: errors[Object.keys(errors)[0]],
      type: 'error',
    });
    return errors;
  }

  return null;
};

const priorityOptions = [
  { id: 'none', name: t('KANBAN.PRIORITY_LABELS.NONE') },
  { id: 'low', name: t('KANBAN.PRIORITY_LABELS.LOW') },
  { id: 'medium', name: t('KANBAN.PRIORITY_LABELS.MEDIUM') },
  { id: 'high', name: t('KANBAN.PRIORITY_LABELS.HIGH') },
  { id: 'urgent', name: t('KANBAN.PRIORITY_LABELS.URGENT') },
];

// Função para obter estilos de prioridade com cores hexadecimais
const getPriorityStyle = optionId => {
  const isSelected = optionId === form.value.item_details.priority;

  const styles = {
    none: {
      selected: {
        borderColor: '#e2e8f0',
        backgroundColor: '#f1f5f9',
        color: '#475569',
      },
      unselected: {
        borderColor: '#e2e8f0',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
      },
    },
    urgent: {
      selected: {
        borderColor: '#fecdd3',
        backgroundColor: '#e11d48',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#fecdd3',
        backgroundColor: '#ffe4e6',
        color: '#e11d48',
      },
    },
    high: {
      selected: {
        borderColor: '#fed7aa',
        backgroundColor: '#ea580c',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#fed7aa',
        backgroundColor: '#ffedd5',
        color: '#ea580c',
      },
    },
    medium: {
      selected: {
        borderColor: '#fef08a',
        backgroundColor: '#eab308',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#fef08a',
        backgroundColor: '#fef9c3',
        color: '#996b00',
      },
    },
    low: {
      selected: {
        borderColor: '#bbf7d0',
        backgroundColor: '#22c55e',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#bbf7d0',
        backgroundColor: '#dcfce7',
        color: '#16a34a',
      },
    },
  };

  const styleConfig =
    styles[optionId]?.[isSelected ? 'selected' : 'unselected'] ||
    styles.none.unselected;

  return {
    borderColor: styleConfig.borderColor,
    backgroundColor: styleConfig.backgroundColor,
    color: styleConfig.color,
  };
};

// Função auxiliar para registrar atividades
const registerActivity = async (itemId, type, details) => {
  try {
    await store.dispatch('kanban/registerActivity', {
      itemId,
      type,
      details,
    });
  } catch (error) {
    // Error handling
  }
};

function closeFloatingPanels() {
  agentDropdownOpen.value = false;
  conversationDropdownOpen.value = false;
}

function handleClose() {
  closeFloatingPanels();
  showManualOfferForm.value = false;
  emit('close');
}

// Modificar handleSubmit para não usar agentsList
const handleSubmit = async e => {
  e.preventDefault();
  const errors = validateForm();
  if (errors) return;

  try {
    loading.value = true;
    let createdConversation = null;

    if (showNewConversationInput.value) {
      createdConversation = await createNewConversationFromForm();
    }

    // Garantir que título e descrição estejam sincronizados
    form.value.item_details.title = form.value.title;
    form.value.item_details.description = form.value.description;

    let finalItemDetails = {};

    // Se estiver editando, começar do snapshot completo para não perder campos
    if (props.isEditing && props.initialData?.item_details) {
      finalItemDetails = JSON.parse(
        JSON.stringify(props.initialData.item_details)
      );
    }

    const fieldsToUpdate = [
      'title',
      'description',
      'value',
      'currency',
      'priority',
      'conversation_id',
      'scheduling_type',
      'scheduled_at',
      'deadline_at',
      'forgotten_after_hours',
      'expected_close_at',
      'custom_attributes',
    ];

    fieldsToUpdate.forEach(field => {
      if (form.value.item_details[field] !== undefined) {
        finalItemDetails[field] = form.value.item_details[field];
      }
    });

    finalItemDetails.title = form.value.title;
    finalItemDetails.description = form.value.description;
    finalItemDetails.notes = formNotes.value.map(
      ({ created_in_form, ...note }) => note
    );
    finalItemDetails.offers = form.value.item_details.offers;

    if (form.value.item_details.offers.length > 0) {
      finalItemDetails.value = form.value.item_details.offers[0].value;
      finalItemDetails.currency = form.value.item_details.offers[0].currency;
    }

    // Criar o payload completo
    const payload = {
      funnel_id: selectedFunnel.value?.id || props.funnelId,
      funnel_stage: form.value.funnel_stage,
      position: props.position,
      assigned_agents: form.value.assigned_agents,
      item_details: finalItemDetails,
      conversation_display_id: form.value.conversation_display_id,
    };

    // custom_attributes deve ser um array de objetos {name, type, value}
    const existingCustomAttributes = Array.isArray(
      finalItemDetails.custom_attributes
    )
      ? finalItemDetails.custom_attributes
      : [];

    // Atualizar custom_attributes com os valores globais preenchidos
    const updatedCustomAttributes = existingCustomAttributes.map(attr => {
      // Se o campo global foi preenchido, atualizar seu valor
      if (globalCustomAttributesValues.value[attr.name] !== undefined) {
        const newValue = globalCustomAttributesValues.value[attr.name];

        // Filtrar arrays vazios
        if (Array.isArray(newValue)) {
          const filteredArray = newValue.filter(
            v => v !== null && v !== undefined && v !== ''
          );
          return { ...attr, value: filteredArray };
        }

        // Para valores simples
        return { ...attr, value: newValue };
      }

      // Manter o atributo sem alteração
      return attr;
    });

    finalItemDetails.custom_attributes = updatedCustomAttributes;

    // Adicionar automações de status
    const enabledAutomations = statusAutomations.value.filter(
      auto => auto.enabled && auto.trigger_value && auto.action
    );
    if (enabledAutomations.length > 0) {
      finalItemDetails.automations = enabledAutomations.map(auto => ({
        id: auto.id,
        enabled: auto.enabled,
        trigger_type: auto.trigger_type,
        trigger_value: auto.trigger_value,
        action: auto.action,
        action_config: auto.action_config || {},
      }));
    }

    // Manter compatibilidade com automação antiga (se existir)
    if (automationCondition.value && automationAction.value) {
      finalItemDetails.automation = {
        condition: automationCondition.value,
        action: automationAction.value,
        config: {},
      };

      // Adicionar configurações específicas da ação
      if (automationAction.value === 'send_email') {
        finalItemDetails.automation.config = {
          email_recipient: automationEmailRecipient.value,
          email_subject: automationEmailSubject.value,
        };
      } else if (automationAction.value === 'create_task') {
        finalItemDetails.automation.config = {
          task_title: automationTaskTitle.value,
          task_assignee: automationTaskAssignee.value,
        };
      }
    }

    const result = props.isEditing
      ? await store.dispatch('kanban/updateItemDetails', {
          itemId: props.initialData.id,
          details: payload, // Enviar o payload completo com funnel_stage
        })
      : await store.dispatch('kanban/createKanbanItem', payload);

    emitter.emit('newToastMessage', {
      message: props.isEditing
        ? 'Item atualizado com sucesso'
        : 'Item criado com sucesso',
      type: 'success',
    });

    emit('saved', result);
    handleClose();

    if (createdConversation) {
      navigateToCreatedConversation(createdConversation);
    }
  } catch (error) {
    const responseErrors = error?.response?.data?.errors;
    const errorMessage = Array.isArray(responseErrors)
      ? responseErrors.join(', ')
      : responseErrors && typeof responseErrors === 'object'
        ? Object.values(responseErrors).flat().filter(Boolean).join(', ')
        : error?.response?.data?.error ||
          error?.message ||
          t('KANBAN.FORM.CONVERSATION.NEW.CREATED_ERROR');

    emitter.emit('newToastMessage', {
      message: errorMessage,
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

// Adicione esta função no script
const formatCurrencyValue = (value, currency) => {
  if (!value) return '-';

  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return value.toString();
  }
};
// Função para copiar o ID do item
const copyItemId = async () => {
  if (!props.initialData?.id) return;

  try {
    await navigator.clipboard.writeText(props.initialData.id.toString());
    emitter.emit('newToastMessage', {
      message: t('KANBAN.ITEM_ID_COPIED') || 'ID do item copiado!',
      type: 'success',
    });
  } catch (error) {
    // Fallback para browsers antigos
    const textArea = document.createElement('textarea');
    textArea.value = props.initialData.id.toString();
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);

    emitter.emit('newToastMessage', {
      message: t('KANBAN.ITEM_ID_COPIED') || 'ID do item copiado!',
      type: 'success',
    });
  }
};

// Dados mock para automação (mantendo compatibilidade)
const automationCondition = ref('');
const automationAction = ref('');

// Configurações específicas das ações
const automationEmailRecipient = ref('');
const automationEmailSubject = ref('');
const automationTaskTitle = ref('');
const automationTaskAssignee = ref('');

// Dados das condições de status
const statusTriggers = ref([
  { id: 'won', name: t('KANBAN.AUTOMATION.STATUS_TRIGGERS.WON') },
  { id: 'lost', name: t('KANBAN.AUTOMATION.STATUS_TRIGGERS.LOST') },
  { id: 'open', name: t('KANBAN.AUTOMATION.STATUS_TRIGGERS.OPEN') },
]);

// Dados das ações de automação
const statusActions = ref([
  {
    id: 'move_to_stage',
    name: t('KANBAN.AUTOMATION.STATUS_ACTIONS.MOVE_TO_STAGE'),
  },
  {
    id: 'move_to_funnel',
    name: t('KANBAN.AUTOMATION.STATUS_ACTIONS.MOVE_TO_FUNNEL'),
  },
  {
    id: 'assign_agent',
    name: t('KANBAN.AUTOMATION.STATUS_ACTIONS.ASSIGN_AGENT'),
  },
  {
    id: 'create_note',
    name: t('KANBAN.AUTOMATION.STATUS_ACTIONS.CREATE_NOTE'),
  },
  {
    id: 'notify_team',
    name: t('KANBAN.AUTOMATION.STATUS_ACTIONS.NOTIFY_TEAM'),
  },
]);

// Dados mock das condições (mantendo compatibilidade)
const automationConditions = ref([
  { id: 'item_created', name: t('KANBAN.AUTOMATION.CONDITIONS.ITEM_CREATED') },
  {
    id: 'stage_changed',
    name: t('KANBAN.AUTOMATION.CONDITIONS.STAGE_CHANGED'),
  },
  {
    id: 'priority_high',
    name: t('KANBAN.AUTOMATION.CONDITIONS.PRIORITY_HIGH'),
  },
  {
    id: 'deadline_approaching',
    name: t('KANBAN.AUTOMATION.CONDITIONS.DEADLINE_APPROACHING'),
  },
  {
    id: 'agent_assigned',
    name: t('KANBAN.AUTOMATION.CONDITIONS.AGENT_ASSIGNED'),
  },
  {
    id: 'offer_selected',
    name: t('KANBAN.AUTOMATION.CONDITIONS.OFFER_SELECTED'),
  },
]);

// Dados mock das ações (mantendo compatibilidade)
const automationActions = ref([
  { id: 'send_email', name: t('KANBAN.AUTOMATION.ACTIONS.SEND_EMAIL') },
  { id: 'create_task', name: t('KANBAN.AUTOMATION.ACTIONS.CREATE_TASK') },
  { id: 'notify_team', name: t('KANBAN.AUTOMATION.ACTIONS.NOTIFY_TEAM') },
  {
    id: 'update_priority',
    name: t('KANBAN.AUTOMATION.ACTIONS.UPDATE_PRIORITY'),
  },
  { id: 'move_stage', name: t('KANBAN.AUTOMATION.ACTIONS.MOVE_STAGE') },
  { id: 'add_comment', name: t('KANBAN.AUTOMATION.ACTIONS.ADD_COMMENT') },
]);

// Funções auxiliares para automação
const getAutomationConditionName = conditionId => {
  const condition = automationConditions.value.find(c => c.id === conditionId);
  return condition ? condition.name : conditionId;
};

const getAutomationActionName = actionId => {
  const action = automationActions.value.find(a => a.id === actionId);
  return action ? action.name : actionId;
};

// Funções auxiliares para automação de status
const getStatusTriggerName = triggerId => {
  const trigger = statusTriggers.value.find(t => t.id === triggerId);
  return trigger ? trigger.name : triggerId;
};

const getStatusActionName = actionId => {
  const action = statusActions.value.find(a => a.id === actionId);
  return action ? action.name : actionId;
};

const getAutomationStagesForFunnel = funnelId => {
  const funnel = availableFunnels.value.find(
    candidate => String(candidate.id) === String(funnelId)
  );

  if (!funnel?.stages) {
    return [];
  }

  return Object.entries(funnel.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
      itemCount:
        stage.item_count ??
        stage.items_count ??
        stage.total_items ??
        stage.count ??
        0,
    }))
    .sort((left, right) => left.position - right.position);
};

const addStatusAutomation = () => {
  const newAutomation = {
    id: `status_automation_${Date.now()}`,
    enabled: false,
    trigger_type: 'status_change',
    trigger_value: '',
    action: '',
    action_config: {},
  };
  statusAutomations.value.push(newAutomation);
};

const removeStatusAutomation = index => {
  statusAutomations.value.splice(index, 1);
};

// Funções para gerenciar agentes
const addAgent = agent => {
  if (!form.value.assigned_agents.some(a => a === agent.id))
    form.value.assigned_agents.push(agent.id);
  agentDropdownOpen.value = false;
  agentSearch.value = '';
};

const removeAgent = agentId => {
  form.value.assigned_agents = form.value.assigned_agents.filter(
    a => a !== agentId
  );
};

// Funções para gerenciar campos globais de lista
const addGlobalListItem = fieldName => {
  if (!globalCustomAttributesValues.value[fieldName]) {
    globalCustomAttributesValues.value[fieldName] = [];
  }
  globalCustomAttributesValues.value[fieldName].push('');
};

const removeGlobalListItem = (fieldName, index) => {
  if (
    globalCustomAttributesValues.value[fieldName] &&
    globalCustomAttributesValues.value[fieldName].length > 1
  ) {
    globalCustomAttributesValues.value[fieldName].splice(index, 1);
  }
};

const getAgentById = agentId => {
  return store.getters['agents/getAgents'].find(a => a.id === agentId);
};

const openAgentDropdown = () => {
  agentDropdownOpen.value = true;
  agentSearch.value = '';
};

const closeAgentDropdown = e => {
  if (!agentSelectorRef.value?.contains(e.target)) {
    agentDropdownOpen.value = false;
  }
};

const addCustomAttribute = () => {
  customAttributes.value.push({ name: '', type: '' });
};

function removeCustomAttribute(index) {
  customAttributes.value.splice(index, 1);
}

// No script, garanta que customAttributes sempre seja um array e, se vazio, adicione pelo menos um campo ao entrar na aba:
watch(activeTab, async tab => {
  closeFloatingPanels();

  if (tab !== 'offers') {
    showManualOfferForm.value = false;
  }

  if (tab === 'custom_fields') {
    // Fazer get para buscar dados do funnel quando abrir a aba de dados adicionais
    try {
      const response = await funnelAPI.get();
      const funnels = response.data || [];

      // Identificar o funil correto: usar o funnel_id do item original se estiver editando, senão usar o selectedFunnel
      const currentFunnelId = props.isEditing
        ? props.initialData?.funnel_id || props.funnelId
        : selectedFunnel.value?.id || props.funnelId;

      // Encontrar o funil correto
      const selectedFunnelData = funnels.find(f => f.id === currentFunnelId);

      // Inicializar campos globais do funil
      if (selectedFunnelData?.global_custom_attributes?.length > 0) {
        globalCustomAttributes.value = [
          ...selectedFunnelData.global_custom_attributes,
        ];

        // Buscar valores existentes dos custom_attributes (que é um array)
        const existingCustomAttrs =
          props.initialData?.item_details?.custom_attributes || [];

        // Inicializar valores dos campos globais com dados existentes
        const existingGlobalValues = {};
        selectedFunnelData.global_custom_attributes.forEach(attr => {
          // Buscar o valor atual deste campo no array de custom_attributes
          const existingAttr = Array.isArray(existingCustomAttrs)
            ? existingCustomAttrs.find(ca => ca.name === attr.name)
            : null;

          if (existingAttr) {
            // Usar o valor existente
            existingGlobalValues[attr.name] = existingAttr.value;
          } else if (attr.is_list) {
            // Para campos de lista novos, inicializar como array com um item vazio
            existingGlobalValues[attr.name] = [''];
          } else {
            // Para campos simples novos, inicializar vazio
            existingGlobalValues[attr.name] = '';
          }
        });
        globalCustomAttributesValues.value = existingGlobalValues;
      } else {
        globalCustomAttributes.value = [];
        globalCustomAttributesValues.value = {};
      }

      // Inicializar customAttributes apenas com campos adicionais (não globais)
      if (customAttributes.value.length === 0) {
        customAttributes.value.push({ name: '', type: '' });
      }
    } catch (error) {
      console.error('Erro ao buscar dados do funnel:', error);
      // Fallback para comportamento anterior se a API falhar
      if (customAttributes.value.length === 0) {
        customAttributes.value.push({ name: '', type: '' });
      }
    }
  }
});

function openConversationDropdown() {
  conversationDropdownOpen.value = true;
  conversationSearch.value = '';
  setTimeout(() => {
    const selector = conversationSelectorRef.value;
    if (selector) {
      const rect = selector.getBoundingClientRect();
      conversationDropdownStyle.value = {
        position: 'fixed',
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
        zIndex: 99999,
      };
    }
    const input = selector?.querySelector('input');
    if (input) input.focus();
  }, 0);
}
function selectConversation(id) {
  form.value.item_details.conversation_id = id;
  form.value.conversation_display_id = id;
  conversationDropdownOpen.value = false;
}
function closeConversationDropdown(e) {
  if (!conversationSelectorRef.value?.contains(e.target)) {
    conversationDropdownOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener('mousedown', closeConversationDropdown);
  document.addEventListener('mousedown', closeAgentDropdown);
});
onUnmounted(() => {
  closeFloatingPanels();
  showManualOfferForm.value = false;
  document.removeEventListener('mousedown', closeConversationDropdown);
  document.removeEventListener('mousedown', closeAgentDropdown);
});
</script>

<template>
  <form
    class="flex flex-col gap-2 max-h-[85vh] overflow-y-auto w-full"
    @submit.prevent="handleSubmit"
  >
    <!-- Tabs -->
    <div class="tabs-container">
      <div class="tabs-scroll-wrapper">
        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[activeTab === 'general' ? 'tab-active' : 'tab-inactive']"
          @click="activeTab = 'general'"
        >
          <div class="flex items-center gap-2">
            <fluent-icon icon="document" size="16" />
            {{ t('KANBAN.TABS.GENERAL') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[activeTab === 'assignment' ? 'tab-active' : 'tab-inactive']"
          @click="activeTab = 'assignment'"
        >
          <div class="flex items-center gap-2">
            <fluent-icon icon="bot" size="16" />
            {{ t('KANBAN.TABS.ASSIGNMENT') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[activeTab === 'scheduling' ? 'tab-active' : 'tab-inactive']"
          @click="activeTab = 'scheduling'"
        >
          <div class="flex items-center gap-2">
            <fluent-icon icon="calendar-clock" size="16" />
            {{ t('KANBAN.TABS.SCHEDULING') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[
            activeTab === 'relationships' ? 'tab-active' : 'tab-inactive',
          ]"
          @click="activeTab = 'relationships'"
        >
          <div class="flex items-center gap-2">
            <fluent-icon icon="attach" size="16" />
            {{ t('KANBAN.TABS.RELATIONSHIPS') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[activeTab === 'notes' ? 'tab-active' : 'tab-inactive']"
          @click="activeTab = 'notes'"
        >
          <div class="flex items-center gap-2">
            <fluent-icon icon="document" size="16" />
            {{ t('KANBAN.TABS.NOTES') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[activeTab === 'offers' ? 'tab-active' : 'tab-inactive']"
          @click="activeTab = 'offers'"
        >
          <div class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-badge-dollar-sign"
            >
              <path
                d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
              />
              <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
              <path d="M12 18V6" />
            </svg>
            {{ t('KANBAN.TABS.OFFERS') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="activeTab === 'custom_fields' ? 'tab-active' : 'tab-inactive'"
          @click.prevent.stop="activeTab = 'custom_fields'"
        >
          <div class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-book-text"
            >
              <path
                d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"
              />
              <path d="M8 11h8" />
              <path d="M8 7h6" />
            </svg>
            {{ t('KANBAN.TABS.CUSTOM_FIELDS') }}
          </div>
        </button>

        <button
          type="button"
          class="tab-button flex-shrink-0"
          :class="[activeTab === 'automation' ? 'tab-active' : 'tab-inactive']"
          @click="activeTab = 'automation'"
        >
          <div class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-rocket-icon lucide-rocket"
            >
              <path
                d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
              />
              <path
                d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
              />
              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
            </svg>
            {{ t('KANBAN.TABS.AUTOMATION') }}
          </div>
        </button>
      </div>
    </div>

    <!-- Conteúdo das Tabs -->
    <div class="tab-content-shell">
      <!-- Tab Dados Gerais -->
      <div v-if="activeTab === 'general'" class="item-form-tab space-y-2.5">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <fluent-icon icon="document" size="16" class="text-woot-500" />
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.TABS.GENERAL') }}
            </h4>
          </div>

          <!-- Item ID Badge and Copy Button -->
          <div
            v-if="isEditing && initialData?.id"
            class="flex items-center gap-1.5"
          >
            <span
              class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            >
              ID: {{ initialData.id }}
            </span>
            <button
              type="button"
              class="p-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
              :title="t('KANBAN.COPY_ITEM_ID')"
              @click="copyItemId"
            >
              <fluent-icon icon="copy" size="14" />
            </button>
          </div>
        </div>

        <!-- Pipeline Fields -->
        <div class="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          <!-- Funil -->
          <div class="space-y-1.5">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('KANBAN.FORM.FUNNEL.LABEL') }}
            </label>
            <select
              v-model="selectedFunnelId"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 dark:border-slate-700 dark:bg-slate-800"
              :disabled="loadingFunnels || availableFunnels.length === 0"
            >
              <option value="" disabled>
                {{
                  loadingFunnels
                    ? t('KANBAN.LOADING_FUNNELS')
                    : t('KANBAN.SELECT_FUNNEL')
                }}
              </option>
              <option
                v-for="funnel in availableFunnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ getFunnelOptionLabel(funnel) }}
              </option>
            </select>
          </div>

          <!-- Etapa -->
          <div class="space-y-1.5">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('KANBAN.FORM.STAGE.LABEL') }}
            </label>
            <select
              v-model="form.funnel_stage"
              class="w-full rounded-lg bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500/20 dark:bg-slate-800"
              :class="
                selectedStageBorderStyle.borderColor
                  ? 'border-2'
                  : 'border border-slate-200 dark:border-slate-700'
              "
              :style="selectedStageBorderStyle"
              :disabled="!selectedFunnel?.id"
            >
              <option value="" disabled>
                {{ t('KANBAN.FORM.STAGE.PLACEHOLDER') }}
              </option>
              <option
                v-for="stage in availableStages"
                :key="stage.id"
                :value="stage.id"
              >
                {{ getStageOptionLabel(stage) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Título -->
        <div class="space-y-1.5">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {{ t('KANBAN.FORM.TITLE.LABEL') }}
          </label>
          <input
            v-model="form.title"
            type="text"
            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 dark:border-slate-700 dark:bg-slate-800"
            :placeholder="t('KANBAN.FORM.TITLE.PLACEHOLDER')"
            required
          />
        </div>

        <!-- Grid com Descrição -->
        <div class="grid grid-cols-1 gap-2">
          <!-- Descrição -->
          <div>
            <div class="editor-panel-shell">
              <div class="editor-panel-header">
                <label
                  class="block text-sm font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('KANBAN.FORM.DESCRIPTION.LABEL') }}
                </label>
              </div>
              <div class="editor-panel-body">
                <Editor
                  v-model="form.description"
                  :placeholder="t('KANBAN.FORM.DESCRIPTION.PLACEHOLDER')"
                  :max-length="1000"
                  :show-character-count="true"
                  :enable-variables="false"
                  :enable-canned-responses="false"
                  :enable-captain-tools="false"
                  :enabled-menu-options="[]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Atribuição -->
      <div v-if="activeTab === 'assignment'" class="item-form-tab space-y-3">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <fluent-icon icon="bot" size="18" class="text-woot-500" />
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.TABS.ASSIGNMENT') }}
            </h4>
          </div>
        </div>

        <div class="space-y-3">
          <div class="space-y-1.5">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('KANBAN.FORM.PRIORITY.LABEL') }}
            </label>
            <div class="grid grid-cols-5 gap-2">
              <label
                v-for="option in priorityOptions"
                :key="option.id"
                class="relative flex cursor-pointer"
              >
                <input
                  v-model="form.item_details.priority"
                  type="radio"
                  :value="option.id"
                  class="peer sr-only"
                />
                <div
                  class="w-full px-3 py-2 text-xs text-center rounded-lg border transition-colors"
                  :style="getPriorityStyle(option.id)"
                >
                  {{ option.name }}
                </div>
              </label>
            </div>
          </div>

          <!-- Agente -->
          <div class="space-y-1.5">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('KANBAN.FORM.AGENT.LABEL') }}
            </label>
            <div ref="agentSelectorRef" class="relative">
              <input
                v-model="agentSearch"
                type="text"
                class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                :placeholder="t('KANBAN.FORM.AGENT.PLACEHOLDER')"
                :disabled="loadingAgents"
                @focus="openAgentDropdown"
                @input="openAgentDropdown"
              />
              <div
                v-if="loadingAgents"
                class="absolute right-2 top-1/2 transform -translate-y-1/2"
              >
                <span class="loading-spinner" />
              </div>

              <!-- Dropdown de agentes -->
              <div
                v-if="agentDropdownOpen"
                class="absolute left-0 top-full w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto z-[99999]"
              >
                <div
                  v-for="agent in filteredAgents"
                  :key="agent.id"
                  class="px-3 py-2 hover:bg-woot-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2"
                  @click="addAgent(agent)"
                >
                  <div
                    class="w-6 h-6 rounded-full bg-woot-500 flex items-center justify-center text-white text-xs font-medium"
                  >
                    {{ agent.name.charAt(0).toUpperCase() }}
                  </div>
                  <span class="text-sm">{{ agent.name }}</span>
                </div>
                <div
                  v-if="filteredAgents.length === 0"
                  class="px-3 py-2 text-slate-400 text-sm"
                >
                  Nenhum agente encontrado
                </div>
              </div>
            </div>

            <!-- Listagem dos agentes selecionados -->
            <div v-if="form.assigned_agents.length" class="space-y-1">
              <div
                class="flex items-center justify-between px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded-lg mb-2"
              >
                <span class="text-sm text-slate-600 dark:text-slate-300">
                  {{ form.assigned_agents.length }} agente{{
                    form.assigned_agents.length !== 1 ? 's' : ''
                  }}
                  selecionado{{ form.assigned_agents.length !== 1 ? 's' : '' }}
                </span>
                <button
                  type="button"
                  class="text-xs text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300"
                  @click="form.assigned_agents = []"
                >
                  Limpar seleção
                </button>
              </div>
              <div
                v-for="agentId in form.assigned_agents"
                :key="agentId"
                class="flex items-center gap-2 px-2 py-1 border border-slate-100 dark:border-slate-800 rounded mb-1"
              >
                <div
                  class="w-6 h-6 rounded-full bg-woot-500 flex items-center justify-center text-white text-xs font-medium"
                >
                  {{
                    getAgentById(agentId)?.name?.charAt(0).toUpperCase() || '?'
                  }}
                </div>
                <span class="text-sm">{{
                  getAgentById(agentId)?.name || 'Agente não encontrado'
                }}</span>
                <button
                  type="button"
                  class="ml-auto text-xs text-ruby-500 hover:text-ruby-600"
                  @click="removeAgent(agentId)"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Agendamento -->
      <div v-if="activeTab === 'scheduling'" class="item-form-tab space-y-3">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <fluent-icon
                icon="calendar-clock"
                size="16"
                class="text-woot-500"
              />
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.FORM.SCHEDULING.LABEL') }}
            </h4>
          </div>
        </div>

        <div class="space-y-3">
          <div class="flex items-center">
            <input
              id="has-scheduling"
              v-model="showScheduling"
              type="checkbox"
              class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
            />
            <label
              for="has-scheduling"
              class="ml-2 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('KANBAN.FORM.SCHEDULING.HAS_SCHEDULING') }}
            </label>
          </div>

          <div v-if="showScheduling" class="space-y-3">
            <div class="flex flex-wrap gap-4">
              <label class="flex items-center">
                <input
                  v-model="schedulingType"
                  type="radio"
                  value="deadline"
                  class="w-4 h-4 text-woot-500 border-slate-300 focus:ring-woot-500"
                />
                <span class="ml-2 text-sm text-slate-700 dark:text-slate-300">
                  {{ t('KANBAN.FORM.SCHEDULING.DEADLINE') }}
                </span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="schedulingType"
                  type="radio"
                  value="schedule"
                  class="w-4 h-4 text-woot-500 border-slate-300 focus:ring-woot-500"
                />
                <span class="ml-2 text-sm text-slate-700 dark:text-slate-300">
                  {{ t('KANBAN.FORM.SCHEDULING.SCHEDULE') }}
                </span>
              </label>
            </div>

            <div v-if="schedulingType === 'schedule'" class="space-y-1.5">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('KANBAN.FORM.SCHEDULING.DATETIME') }}
              </label>
              <input
                v-model="form.item_details.scheduled_at"
                type="datetime-local"
                class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              />
            </div>

            <div v-else class="space-y-1.5">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('KANBAN.FORM.SCHEDULING.DEADLINE_DATE') }}
              </label>
              <input
                v-model="form.item_details.deadline_at"
                type="date"
                class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="space-y-1.5">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('KANBAN.FORM.SCHEDULING.FORGOTTEN_AFTER_HOURS') }}
              </label>
              <input
                v-model.number="form.item_details.forgotten_after_hours"
                type="number"
                min="1"
                step="1"
                class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                :placeholder="
                  t('KANBAN.FORM.SCHEDULING.FORGOTTEN_AFTER_HOURS_PLACEHOLDER')
                "
              />
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ t('KANBAN.FORM.SCHEDULING.FORGOTTEN_AFTER_HOURS_HELP') }}
              </p>
            </div>

            <div class="space-y-1.5">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('KANBAN.FORM.SCHEDULING.EXPECTED_CLOSE_AT') }}
              </label>
              <input
                v-model="form.item_details.expected_close_at"
                type="date"
                class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              />
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ t('KANBAN.FORM.SCHEDULING.EXPECTED_CLOSE_AT_HELP') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Relacionamentos -->
      <div v-if="activeTab === 'relationships'" class="item-form-tab space-y-3">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <fluent-icon icon="attach" size="18" class="text-woot-500" />
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.TABS.RELATIONSHIPS') }}
            </h4>
          </div>
        </div>

        <div class="space-y-3">
          <!-- Conversa -->
          <div class="space-y-3">
            <div class="flex items-center">
              <input
                id="has-conversation"
                v-model="showConversationInput"
                type="checkbox"
                class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
              />
              <label
                for="has-conversation"
                class="ml-2 text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('KANBAN.FORM.CONVERSATION.HAS_CONVERSATION') }}
              </label>
            </div>

            <div v-if="showConversationInput">
              <div ref="conversationSelectorRef" class="relative">
                <div
                  class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                  tabindex="0"
                  @click="openConversationDropdown"
                >
                  <span v-if="selectedConversation">
                    {{ getConversationSummary(selectedConversation) }}
                  </span>
                  <span v-else class="text-slate-400">
                    {{ t('KANBAN.FORM.CONVERSATION.PLACEHOLDER') }}
                  </span>
                </div>
                <teleport to="body">
                  <div
                    v-if="conversationDropdownOpen"
                    :style="conversationDropdownStyle"
                    class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-96 overflow-y-auto"
                    @mousedown.stop
                  >
                    <input
                      v-model="conversationSearch"
                      type="text"
                      class="w-full px-3 py-2 mb-2 text-sm border-b border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none"
                      :placeholder="
                        t('KANBAN.FORM.CONVERSATION.SEARCH_PLACEHOLDER')
                      "
                    />
                    <div class="max-h-72 overflow-y-auto">
                      <div
                        v-for="conversation in filteredConversations"
                        :key="conversation.id"
                        class="px-3 py-2 hover:bg-woot-50 dark:hover:bg-slate-700 cursor-pointer text-sm"
                        @click="selectConversation(conversation.id)"
                      >
                        {{ getConversationSummary(conversation) }}
                      </div>
                      <div
                        v-if="filteredConversations.length === 0"
                        class="px-3 py-2 text-slate-400 text-sm"
                      >
                        {{ t('KANBAN.FORM.CONVERSATION.EMPTY') }}
                      </div>
                    </div>
                  </div>
                </teleport>
              </div>
            </div>

            <div class="space-y-3">
              <button
                type="button"
                :aria-pressed="showNewConversationInput"
                class="w-full rounded-lg border p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                :class="
                  showNewConversationInput
                    ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                    : 'border-slate-200 bg-white hover:border-woot-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-woot-700'
                "
                @click="showNewConversationInput = !showNewConversationInput"
              >
                <div class="flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <p
                      class="text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {{ t('KANBAN.FORM.CONVERSATION.NEW.TITLE') }}
                    </p>
                    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {{ t('KANBAN.FORM.CONVERSATION.NEW.DESCRIPTION') }}
                    </p>
                  </div>
                  <span
                    class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                    :class="
                      showNewConversationInput
                        ? 'border-woot-500 bg-woot-500'
                        : 'border-slate-300 dark:border-slate-600'
                    "
                  >
                    <fluent-icon
                      v-if="showNewConversationInput"
                      icon="checkmark"
                      size="12"
                      class="text-white"
                    />
                  </span>
                </div>
              </button>

              <div
                v-if="showNewConversationInput"
                class="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60"
              >
                <div class="space-y-2">
                  <label
                    class="text-xs font-medium text-slate-500 dark:text-slate-400"
                  >
                    {{ t('KANBAN.FORM.CONVERSATION.NEW.CONTACT_LABEL') }}
                  </label>
                  <div class="flex gap-2">
                    <input
                      v-model="newConversationContactSearch"
                      type="text"
                      class="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 dark:border-slate-700 dark:bg-slate-800"
                      :placeholder="
                        t('KANBAN.FORM.CONVERSATION.NEW.CONTACT_PLACEHOLDER')
                      "
                      @keyup.enter="searchNewConversationContacts"
                    />
                    <Button
                      variant="solid"
                      color="blue"
                      size="sm"
                      :is-loading="newConversationLoadingContacts"
                      @click="searchNewConversationContacts"
                    >
                      {{ t('KANBAN.FORM.CONVERSATION.NEW.SEARCH') }}
                    </Button>
                  </div>
                </div>

                <div
                  class="max-h-52 overflow-y-auto rounded-md border border-slate-100 bg-white dark:border-slate-700 dark:bg-slate-900/40"
                >
                  <div
                    v-if="newConversationLoadingContacts"
                    class="px-3 py-5 text-center text-sm text-slate-400"
                  >
                    {{ t('KANBAN.FORM.CONVERSATION.NEW.LOADING_CONTACTS') }}
                  </div>
                  <div
                    v-else-if="newConversationContacts.length === 0"
                    class="px-3 py-5 text-center text-sm text-slate-400"
                  >
                    {{ t('KANBAN.FORM.CONVERSATION.NEW.NO_CONTACTS') }}
                  </div>
                  <div
                    v-else
                    class="divide-y divide-slate-100 dark:divide-slate-800"
                  >
                    <button
                      v-for="contact in newConversationContacts"
                      :key="contact.id"
                      type="button"
                      class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                      :class="{
                        'bg-woot-50 dark:bg-woot-900/30':
                          newConversationSelectedContact?.id === contact.id,
                      }"
                      @click="selectNewConversationContact(contact)"
                    >
                      <span class="min-w-0">
                        <span
                          class="block truncate font-medium text-slate-800 dark:text-slate-100"
                        >
                          {{
                            contact.name ||
                            t('KANBAN.FORM.CONVERSATION.NEW.UNNAMED_CONTACT')
                          }}
                        </span>
                        <span
                          class="block truncate text-xs text-slate-500 dark:text-slate-400"
                        >
                          {{ getContactSubtitle(contact) }}
                        </span>
                      </span>
                      <fluent-icon
                        v-if="newConversationSelectedContact?.id === contact.id"
                        icon="checkmark-circle"
                        size="16"
                        class="flex-shrink-0 text-woot-500"
                      />
                    </button>
                  </div>
                </div>

                <Button
                  v-if="
                    newConversationContactSearch.trim() &&
                    !newConversationSelectedContact
                  "
                  variant="ghost"
                  color="blue"
                  size="sm"
                  :is-loading="newConversationLoadingInboxes"
                  @click="createContactFromNewConversationInput"
                >
                  {{
                    t('KANBAN.FORM.CONVERSATION.NEW.CREATE_CONTACT', {
                      value: formatNewContactInput(
                        newConversationContactSearch
                      ),
                    })
                  }}
                </Button>

                <div v-if="newConversationSelectedContact" class="space-y-2">
                  <label
                    class="text-xs font-medium text-slate-500 dark:text-slate-400"
                  >
                    {{ t('KANBAN.FORM.CONVERSATION.NEW.INBOX_LABEL') }}
                  </label>
                  <div
                    class="max-h-40 overflow-y-auto rounded-md border border-slate-100 bg-white dark:border-slate-700 dark:bg-slate-900/40"
                  >
                    <div
                      v-if="newConversationLoadingInboxes"
                      class="px-3 py-4 text-center text-sm text-slate-400"
                    >
                      {{ t('KANBAN.FORM.CONVERSATION.NEW.LOADING_INBOXES') }}
                    </div>
                    <div
                      v-else-if="newConversationInboxes.length === 0"
                      class="px-3 py-4 text-center text-sm text-slate-400"
                    >
                      {{ t('KANBAN.FORM.CONVERSATION.NEW.NO_INBOXES') }}
                    </div>
                    <div
                      v-else
                      class="divide-y divide-slate-100 dark:divide-slate-800"
                    >
                      <button
                        v-for="inbox in newConversationInboxes"
                        :key="inbox.id"
                        type="button"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                        :class="{
                          'bg-woot-50 dark:bg-woot-900/30':
                            newConversationSelectedInbox?.id === inbox.id,
                        }"
                        @click="newConversationSelectedInbox = inbox"
                      >
                        <span
                          class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-slate-300 dark:border-slate-600"
                        >
                          <span
                            v-if="newConversationSelectedInbox?.id === inbox.id"
                            class="h-2.5 w-2.5 rounded-full bg-woot-500"
                          />
                        </span>
                        <span class="min-w-0">
                          <span
                            class="block truncate font-medium text-slate-800 dark:text-slate-100"
                          >
                            {{ inbox.name }}
                          </span>
                          <span
                            class="block truncate text-xs text-slate-500 dark:text-slate-400"
                          >
                            {{ inbox.channelType }}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="space-y-2">
                  <label
                    class="text-xs font-medium text-slate-500 dark:text-slate-400"
                  >
                    {{ t('KANBAN.FORM.CONVERSATION.NEW.MESSAGE_LABEL') }}
                  </label>
                  <textarea
                    v-model="newConversationMessage"
                    rows="3"
                    class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 dark:border-slate-700 dark:bg-slate-800"
                    :placeholder="
                      t('KANBAN.FORM.CONVERSATION.NEW.MESSAGE_PLACEHOLDER')
                    "
                  />
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ t('KANBAN.FORM.CONVERSATION.NEW.SAVE_HINT') }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="relatedConversations.length > 0" class="space-y-2">
              <div class="space-y-1">
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                >
                  {{ t('KANBAN.FORM.CONVERSATION.RELATED_LIST_TITLE') }}
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ t('KANBAN.FORM.CONVERSATION.RELATED_LIST_DESCRIPTION') }}
                </p>
              </div>

              <div class="space-y-2">
                <div
                  v-for="conversation in relatedConversations"
                  :key="`related-conversation-${conversation.id}`"
                  class="flex items-start justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2"
                >
                  <div class="min-w-0">
                    <p
                      class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate"
                    >
                      {{ `#${conversation.id} - ${conversation.label}` }}
                    </p>

                    <div class="mt-2 flex flex-wrap gap-1.5">
                      <span
                        v-for="source in conversation.sources"
                        :key="`${conversation.id}-${source}`"
                        class="inline-flex items-center rounded-full bg-white dark:bg-slate-700 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600"
                      >
                        {{ getConversationSourceLabel(source) }}
                      </span>
                    </div>
                  </div>

                  <span
                    v-if="conversation.isPrimary"
                    class="inline-flex items-center rounded-full bg-woot-50 dark:bg-woot-900/30 px-2 py-0.5 text-xs font-medium text-woot-700 dark:text-woot-200"
                  >
                    {{ t('KANBAN.FORM.CONVERSATION.RELATED_PRIMARY_BADGE') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'notes'" class="item-form-tab space-y-3">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <fluent-icon icon="document" size="18" class="text-woot-500" />
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.TABS.NOTES') }}
            </h4>
          </div>
        </div>

        <div class="space-y-3">
          <Editor
            v-model="formNoteDraft"
            :placeholder="t('KANBAN.FORM.NOTES.PLACEHOLDER')"
            :max-length="2000"
            :show-character-count="true"
            :enable-variables="false"
            :enable-canned-responses="false"
            :enable-captain-tools="false"
            :enabled-menu-options="[]"
          />

          <div class="flex justify-end">
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :disabled="!formNoteDraft.trim()"
              @click="addFormNote"
            >
              {{ t('ADD') }}
            </Button>
          </div>
        </div>

        <div v-if="formNotes.length > 0" class="space-y-3">
          <div
            v-for="note in formNotes"
            :key="note.id"
            class="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div
                  class="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2"
                >
                  <span class="font-medium text-slate-700 dark:text-slate-200">
                    {{ note.author || t('KANBAN.SYSTEM') }}
                  </span>
                  <span v-if="note.created_at">
                    {{ formatFormNoteDate(note.created_at) }}
                  </span>
                  <span
                    v-if="note.created_in_form"
                    class="rounded-full bg-woot-50 dark:bg-woot-900/30 px-2 py-0.5 text-[11px] font-medium text-woot-700 dark:text-woot-200"
                  >
                    Draft
                  </span>
                </div>

                <div
                  class="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words"
                >
                  {{ note.text }}
                </div>
              </div>

              <button
                v-if="note.created_in_form"
                type="button"
                class="p-1.5 rounded-md text-slate-500 hover:text-ruby-500 hover:bg-ruby-50 dark:hover:bg-ruby-900/20 transition-colors"
                @click="removePendingFormNote(note.id)"
              >
                <fluent-icon icon="delete" size="14" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Ofertas -->
      <div v-if="activeTab === 'offers'" class="item-form-tab space-y-2.5">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-badge-dollar-sign text-woot-500"
              >
                <path
                  d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
                />
                <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                <path d="M12 18V6" />
              </svg>
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.TABS.OFFERS') }}
            </h4>
          </div>
        </div>

        <div class="space-y-2.5">
          <div class="flex items-center">
            <input
              id="has-offers"
              v-model="showOffersInput"
              type="checkbox"
              class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
            />
            <label
              for="has-offers"
              class="ml-2 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('KANBAN.FORM.OFFERS.HAS_OFFERS') }}
            </label>
          </div>

          <div v-if="showOffersInput">
            <div class="section-card-shell">
              <!-- Conteúdo do Card -->
              <div
                class="section-card-body divide-y divide-slate-100 dark:divide-slate-700/50"
              >
                <!-- Loading State -->
                <div
                  v-if="loadingOffers"
                  class="flex items-center justify-center py-6"
                >
                  <span class="loading-spinner" />
                </div>

                <!-- Empty State -->
                <div
                  v-else-if="!availableOffers.length"
                  class="compact-empty-state"
                >
                  <fluent-icon
                    icon="tag"
                    size="28"
                    class="mb-1.5 text-slate-400"
                  />
                  <p class="text-sm text-center">Nenhuma oferta encontrada</p>
                  <p class="text-xs text-center mt-1 text-slate-500">
                    Crie ofertas na seção de ofertas
                  </p>
                </div>

                <!-- Lista de Ofertas Disponíveis -->
                <div v-else class="space-y-2.5 p-3">
                  <div
                    v-for="offer in availableOffers"
                    :key="offer.id"
                    class="offer-selection-card"
                    :class="{
                      'border-woot-300 dark:border-woot-600 ring-2 ring-woot-200 dark:ring-woot-800':
                        isOfferSelected(offer.id),
                      'border-slate-200 dark:border-slate-700':
                        !isOfferSelected(offer.id),
                    }"
                    @click="toggleOfferSelection(offer)"
                  >
                    <div class="flex items-center gap-2.5">
                      <!-- Checkbox de Seleção -->
                      <div class="flex items-center px-3 py-2.5">
                        <input
                          type="checkbox"
                          :checked="isOfferSelected(offer.id)"
                          class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
                          @click.stop
                          @change="toggleOfferSelection(offer)"
                        />
                      </div>

                      <!-- Imagem da Oferta ou Placeholder -->
                      <div class="offer-preview-shell">
                        <img
                          v-if="offer.image_url"
                          :src="offer.image_url"
                          :alt="offer.name"
                          class="w-full h-full object-cover"
                        />
                        <fluent-icon
                          v-else
                          icon="image"
                          size="32"
                          class="text-slate-300 dark:text-slate-700"
                        />
                      </div>

                      <!-- Informações da Oferta -->
                      <div class="min-w-0 flex-1 py-2.5 pr-3">
                        <div class="mb-1.5 flex items-center gap-2">
                          <span
                            class="inline-block px-1.5 py-0.5 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded"
                          >
                            #{{ offer.id }}
                          </span>
                          <span
                            v-if="offer.type"
                            class="inline-block px-1.5 py-0.5 text-xs font-medium rounded"
                            :class="[
                              offer.type === 'service'
                                ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                : 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
                            ]"
                          >
                            {{
                              offer.type === 'service'
                                ? t('KANBAN.FORM.OFFERS.TYPE_SERVICE')
                                : t('KANBAN.FORM.OFFERS.TYPE_PRODUCT')
                            }}
                          </span>
                        </div>
                        <h4
                          class="mb-1.5 truncate text-sm font-semibold text-slate-900 dark:text-white"
                        >
                          {{ offer.name }}
                        </h4>

                        <!-- Valor e moeda -->
                        <div
                          class="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-50 dark:bg-green-900/20 rounded"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-green-600 dark:text-green-400"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path
                              d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"
                            />
                            <path d="M12 18V6" />
                          </svg>
                          <span
                            class="text-xs font-bold text-green-700 dark:text-green-300"
                          >
                            {{ offer.discount_percentage }} {{ offer.currency }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Botão para Adicionar Oferta Manual (sempre visível) -->
                <div v-if="!loadingOffers" class="p-4">
                  <button
                    v-if="!showManualOfferForm"
                    type="button"
                    class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-600 transition-colors hover:border-woot-500 hover:text-woot-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-woot-500 dark:hover:text-woot-400"
                    @click="showManualOfferForm = true"
                  >
                    <fluent-icon icon="add" size="16" />
                    {{ t('KANBAN.FORM.OFFERS.ADD_MANUAL_OFFER') }}
                  </button>

                  <!-- Formulário de Oferta Manual -->
                  <div
                    v-if="showManualOfferForm"
                    class="rounded-lg border border-woot-300 bg-white p-3 dark:border-woot-600 dark:bg-slate-800"
                  >
                    <div class="mb-2 flex items-center justify-between">
                      <h5
                        class="text-sm font-medium text-slate-700 dark:text-slate-300"
                      >
                        {{ t('KANBAN.FORM.OFFERS.NEW_MANUAL_OFFER') }}
                      </h5>
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        @click="cancelManualOffer"
                      >
                        <fluent-icon icon="dismiss" size="16" />
                      </button>
                    </div>

                    <div class="space-y-2.5">
                      <label
                        class="block text-xs text-slate-600 dark:text-slate-400"
                      >
                        Descrição
                      </label>
                      <input
                        v-model="manualOffer.description"
                        type="text"
                        class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                        placeholder="Ex: Consultoria Premium"
                      />
                    </div>

                    <div class="space-y-2.5">
                      <div class="grid grid-cols-2 gap-2">
                        <label
                          class="block text-xs text-slate-600 dark:text-slate-400"
                        >
                          Valor
                        </label>
                        <label
                          class="block text-xs text-slate-600 dark:text-slate-400"
                        >
                          Moeda
                        </label>
                      </div>
                      <div class="grid grid-cols-2 gap-2">
                        <input
                          v-model="manualOffer.value"
                          type="number"
                          step="0.01"
                          class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                          placeholder="0.00"
                        />
                        <select
                          v-model="manualOffer.currency.code"
                          class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                          @change="
                            manualOffer.currency = {
                              symbol:
                                manualOffer.currency.code === 'BRL'
                                  ? 'R$'
                                  : manualOffer.currency.code === 'USD'
                                    ? '$'
                                    : '€',
                              code: manualOffer.currency.code,
                              locale:
                                manualOffer.currency.code === 'BRL'
                                  ? 'pt-BR'
                                  : manualOffer.currency.code === 'USD'
                                    ? 'en-US'
                                    : 'de-DE',
                            }
                          "
                        >
                          <option value="BRL">BRL (R$)</option>
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                        </select>
                      </div>
                    </div>

                    <div class="flex gap-2 pt-2">
                      <button
                        type="button"
                        class="flex-1 px-3 py-2 bg-woot-500 hover:bg-woot-600 text-white text-sm rounded-lg transition-colors"
                        @click="addManualOffer"
                      >
                        Adicionar
                      </button>
                      <button
                        type="button"
                        class="px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-sm rounded-lg transition-colors"
                        @click="cancelManualOffer"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Ofertas Selecionadas -->
                <div
                  v-if="form.item_details.offers.length > 0"
                  class="bg-slate-50 p-3 dark:bg-slate-800/50"
                >
                  <h5
                    class="mb-2 text-xs font-medium text-slate-600 dark:text-slate-400"
                  >
                    Ofertas Selecionadas ({{ form.item_details.offers.length }})
                  </h5>
                  <div class="space-y-1.5">
                    <div
                      v-for="(selectedOffer, index) in form.item_details.offers"
                      :key="index"
                      class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-600 dark:bg-slate-700"
                    >
                      <fluent-icon
                        :icon="
                          selectedOffer.manual ? 'edit' : 'checkmark-circle'
                        "
                        size="16"
                        :class="
                          selectedOffer.manual
                            ? 'text-blue-500'
                            : 'text-green-500'
                        "
                        class="flex-shrink-0"
                      />
                      <span
                        class="text-sm text-slate-700 dark:text-slate-300 truncate flex-1"
                      >
                        {{ selectedOffer.description }}
                      </span>
                      <span
                        v-if="selectedOffer.manual"
                        class="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded"
                      >
                        Manual
                      </span>
                      <span
                        v-if="selectedOffer.is_default"
                        class="text-xs px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded"
                      >
                        {{ t('KANBAN.FORM.OFFERS.DEFAULT_BADGE') }}
                      </span>
                      <span
                        v-if="selectedOffer.is_winner"
                        class="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded"
                      >
                        {{ t('KANBAN.FORM.OFFERS.WINNER_BADGE') }}
                      </span>
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        {{
                          formatCurrencyValue(
                            selectedOffer.value,
                            selectedOffer.currency
                          )
                        }}
                      </span>
                      <button
                        v-if="!selectedOffer.is_default"
                        type="button"
                        class="text-[11px] font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
                        @click="setDefaultOffer(index)"
                      >
                        {{ t('KANBAN.FORM.OFFERS.SET_DEFAULT') }}
                      </button>
                      <button
                        type="button"
                        class="text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300 transition-colors ml-1"
                        title="Remover oferta"
                        @click="removeOffer(index)"
                      >
                        <fluent-icon icon="dismiss" size="14" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Campos Personalizados -->
      <div
        v-if="activeTab === 'custom_fields'"
        class="item-form-tab space-y-4"
        @click.prevent.stop
      >
        <!-- Campos Globais do Funil -->
        <div class="space-y-4">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('KANBAN.FORM.CUSTOM_FIELDS.GLOBAL_FIELDS') }}
          </h4>
          <div v-if="globalCustomAttributes.length > 0" class="space-y-4">
            <div
              v-for="(attr, idx) in globalCustomAttributes"
              :key="`global-${idx}`"
              class="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-3 mb-3">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ attr.name }}
                </span>
                <span
                  class="text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded"
                >
                  {{ getFieldTypeLabel(attr.type) }}
                  {{
                    attr.is_list
                      ? t('KANBAN.FORM.CUSTOM_FIELDS.LIST_INDICATOR')
                      : ''
                  }}
                </span>
              </div>

              <!-- Campo simples -->
              <div v-if="!attr.is_list">
                <input
                  v-model="globalCustomAttributesValues[attr.name]"
                  :type="
                    attr.type === 'date'
                      ? 'date'
                      : attr.type === 'number'
                        ? 'number'
                        : 'text'
                  "
                  class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                  :placeholder="`${t('KANBAN.FORM.CUSTOM_FIELDS.ENTER_VALUE_FOR')} ${attr.name}`"
                />
              </div>

              <!-- Campo de lista -->
              <div v-else class="space-y-2">
                <div
                  v-for="(value, valueIdx) in globalCustomAttributesValues[
                    attr.name
                  ] || []"
                  :key="`global-${idx}-value-${valueIdx}`"
                  class="flex items-center gap-2"
                >
                  <input
                    v-model="globalCustomAttributesValues[attr.name][valueIdx]"
                    :type="
                      attr.type === 'date'
                        ? 'date'
                        : attr.type === 'number'
                          ? 'number'
                          : 'text'
                    "
                    class="flex-1 px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                    :placeholder="`${t('KANBAN.FORM.CUSTOM_FIELDS.ITEM_NUMBER_FOR')} ${valueIdx + 1} ${t('KANBAN.FORM.CUSTOM_FIELDS.FOR_FIELD')} ${attr.name}`"
                  />
                  <button
                    type="button"
                    class="p-2 text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300 rounded transition-colors"
                    :disabled="
                      (globalCustomAttributesValues[attr.name] || []).length <=
                      1
                    "
                    @click="() => removeGlobalListItem(attr.name, valueIdx)"
                  >
                    <fluent-icon icon="dismiss" size="14" />
                  </button>
                </div>
                <button
                  type="button"
                  class="mt-2 px-3 py-1 bg-woot-500 hover:bg-woot-600 text-white text-sm rounded transition-colors flex items-center gap-1"
                  @click="() => addGlobalListItem(attr.name)"
                >
                  {{ t('KANBAN.FORM.CUSTOM_FIELDS.ADD_ITEM') }}
                  <fluent-icon icon="add" size="12" />
                </button>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4">
            <p class="text-sm text-slate-500">
              {{ t('KANBAN.FORM.CUSTOM_FIELDS.NO_GLOBAL_FIELDS') }}
            </p>
          </div>
        </div>

        <!-- Campos Personalizados Adicionais -->
        <div class="space-y-4">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('KANBAN.FORM.CUSTOM_FIELDS.ADDITIONAL_FIELDS') }}
          </h4>

          <div v-if="customAttributes.length === 0" class="text-center py-4">
            <p class="text-sm text-slate-500">
              {{ t('KANBAN.FORM.CUSTOM_FIELDS.EMPTY') }}
            </p>
          </div>

          <div v-else class="space-y-4" @click.prevent.stop>
            <div
              v-for="(attr, idx) in customAttributes"
              :key="idx"
              class="flex gap-2 items-center"
              @click.prevent.stop
            >
              <input
                v-model="attr.name"
                type="text"
                class="w-1/2 px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                :placeholder="t('KANBAN.FORM.CUSTOM_FIELDS.KEY_PLACEHOLDER')"
              />
              <select
                v-model="attr.type"
                class="w-1/2 px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              >
                <option value="string">
                  {{ t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_TEXT') }}
                </option>
                <option value="number">
                  {{ t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_NUMBER') }}
                </option>
                <option value="date">
                  {{ t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_DATE') }}
                </option>
                <option value="boolean">
                  {{ t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_BOOLEAN') }}
                </option>
              </select>
              <button
                type="button"
                class="p-1 text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300 rounded transition-colors"
                @click="removeCustomAttribute(idx)"
              >
                ✕
              </button>
            </div>
            <button
              type="button"
              class="mt-2 px-3 py-1 bg-woot-500 hover:bg-woot-600 text-white rounded transition-colors"
              @click="addCustomAttribute"
            >
              {{ t('KANBAN.FORM.CUSTOM_FIELDS.ADD_FIELD') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tab Automação -->
      <div v-if="activeTab === 'automation'" class="item-form-tab space-y-3">
        <div class="tab-section-header">
          <div class="tab-section-heading">
            <div class="tab-section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-rocket-icon lucide-rocket text-woot-500"
              >
                <path
                  d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
                />
                <path
                  d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
                />
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
              </svg>
            </div>
            <h4 class="tab-section-title">
              {{ t('KANBAN.TABS.AUTOMATION') }}
            </h4>
          </div>
        </div>

        <div class="space-y-4">
          <!-- Título da seção -->
          <div class="text-left">
            <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ t('KANBAN.AUTOMATION.STATUS_AUTOMATION_TITLE') }}
            </h4>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{ t('KANBAN.AUTOMATION.STATUS_AUTOMATION_DESCRIPTION') }}
            </p>
          </div>

          <!-- Lista de Automations de Status -->
          <div class="space-y-3">
            <div
              v-for="(automation, index) in statusAutomations"
              :key="automation.id"
              class="automation-card-shell"
            >
              <!-- Header da automação -->
              <div class="mb-2.5 flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <input
                    :id="`automation-${index}-enabled`"
                    v-model="automation.enabled"
                    type="checkbox"
                    class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
                  />
                  <label
                    :for="`automation-${index}-enabled`"
                    class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.AUTOMATION_ENABLED') }}
                  </label>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    class="text-xs px-2 py-1 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded"
                  >
                    #{{ index + 1 }}
                  </span>
                  <button
                    type="button"
                    class="p-1 text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300 rounded transition-colors"
                    :title="t('KANBAN.ACTIONS.DELETE')"
                    @click="removeStatusAutomation(index)"
                  >
                    <fluent-icon icon="dismiss" size="16" />
                  </button>
                </div>
              </div>

              <!-- Configuração da Trigger -->
              <div class="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div class="space-y-1.5">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.WHEN_STATUS_CHANGES_TO') }}
                  </label>
                  <select
                    v-model="automation.trigger_value"
                    class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                    :disabled="!automation.enabled"
                  >
                    <option value="" disabled>
                      {{ t('KANBAN.AUTOMATION.SELECT_STATUS') }}
                    </option>
                    <option
                      v-for="trigger in statusTriggers"
                      :key="trigger.id"
                      :value="trigger.id"
                    >
                      {{ trigger.name }}
                    </option>
                  </select>
                </div>

                <div class="space-y-1.5">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.THEN_EXECUTE') }}
                  </label>
                  <select
                    v-model="automation.action"
                    class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                    :disabled="!automation.enabled"
                  >
                    <option value="" disabled>
                      {{ t('KANBAN.AUTOMATION.SELECT_ACTION') }}
                    </option>
                    <option
                      v-for="action in statusActions"
                      :key="action.id"
                      :value="action.id"
                    >
                      {{ action.name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Configurações específicas da ação -->
              <div
                v-if="automation.enabled && automation.action"
                class="rounded-lg border border-slate-200 bg-white p-2.5 dark:border-slate-600 dark:bg-slate-800"
              >
                <!-- Configuração: Mover para etapa -->
                <div
                  v-if="automation.action === 'move_to_stage'"
                  class="space-y-1.5"
                >
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.SELECT_STAGE') }}
                  </label>
                  <select
                    v-model="automation.action_config.stage"
                    class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                  >
                    <option value="" disabled>
                      {{ t('KANBAN.AUTOMATION.SELECT_STAGE') }}
                    </option>
                    <option
                      v-for="stage in availableStages"
                      :key="stage.id"
                      :value="stage.id"
                    >
                      {{ stage.name }}
                    </option>
                  </select>
                </div>

                <!-- Configuração: Mover para funil -->
                <div
                  v-if="automation.action === 'move_to_funnel'"
                  class="grid grid-cols-1 gap-3 md:grid-cols-2"
                >
                  <div class="space-y-1.5">
                    <label
                      class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {{ t('KANBAN.AUTOMATION.SELECT_FUNNEL') }}
                    </label>
                    <select
                      v-model="automation.action_config.funnel_id"
                      class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                    >
                      <option value="" disabled>
                        {{ t('KANBAN.AUTOMATION.SELECT_FUNNEL') }}
                      </option>
                      <option
                        v-for="funnel in availableFunnels"
                        :key="funnel.id"
                        :value="funnel.id"
                      >
                        {{ getFunnelOptionLabel(funnel) }}
                      </option>
                    </select>
                  </div>

                  <div class="space-y-1.5">
                    <label
                      class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {{ t('KANBAN.AUTOMATION.SELECT_STAGE') }}
                    </label>
                    <select
                      v-model="automation.action_config.stage"
                      class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                      :disabled="!automation.action_config.funnel_id"
                    >
                      <option value="" disabled>
                        {{ t('KANBAN.AUTOMATION.SELECT_STAGE') }}
                      </option>
                      <option
                        v-for="stage in getAutomationStagesForFunnel(
                          automation.action_config.funnel_id
                        )"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ getStageOptionLabel(stage) }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Configuração: Atribuir agente -->
                <div
                  v-if="automation.action === 'assign_agent'"
                  class="space-y-1.5"
                >
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.SELECT_AGENT') }}
                  </label>
                  <select
                    v-model="automation.action_config.agent_id"
                    class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                  >
                    <option value="" disabled>
                      {{ t('KANBAN.AUTOMATION.SELECT_AGENT') }}
                    </option>
                    <option
                      v-for="agent in store.getters['agents/getAgents']"
                      :key="agent.id"
                      :value="agent.id"
                    >
                      {{ agent.name }}
                    </option>
                  </select>
                </div>

                <!-- Configuração: Criar nota -->
                <div
                  v-if="automation.action === 'create_note'"
                  class="space-y-1.5"
                >
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.NOTE_TEXT') }}
                  </label>
                  <textarea
                    v-model="automation.action_config.note_text"
                    class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                    :placeholder="t('KANBAN.AUTOMATION.NOTE_TEXT_PLACEHOLDER')"
                    rows="3"
                  />
                </div>

                <!-- Configuração: Notificar equipe -->
                <div
                  v-if="automation.action === 'notify_team'"
                  class="space-y-1.5"
                >
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('KANBAN.AUTOMATION.NOTIFICATION_MESSAGE') }}
                  </label>
                  <textarea
                    v-model="automation.action_config.message"
                    class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                    :placeholder="
                      t('KANBAN.AUTOMATION.NOTIFICATION_MESSAGE_PLACEHOLDER')
                    "
                    rows="2"
                  />
                </div>
              </div>

              <!-- Preview da automação -->
              <div
                v-if="
                  automation.enabled &&
                  automation.trigger_value &&
                  automation.action
                "
                class="mt-3 p-2 bg-green-50 dark:bg-green-900/10 rounded border border-green-200 dark:border-green-800"
              >
                <div class="text-xs text-green-700 dark:text-green-300">
                  <strong>{{ t('KANBAN.AUTOMATION.PREVIEW') }}:</strong>
                  {{ t('KANBAN.AUTOMATION.WHEN_STATUS_CHANGES_TO') }}
                  <span class="font-medium">{{
                    getStatusTriggerName(automation.trigger_value)
                  }}</span>
                  {{ t('KANBAN.AUTOMATION.THEN_EXECUTE') }}
                  <span class="font-medium">{{
                    getStatusActionName(automation.action)
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Botão para adicionar nova automação -->
          <div class="text-center">
            <button
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 bg-woot-500 hover:bg-woot-600 text-white text-sm rounded-lg transition-colors"
              @click="addStatusAutomation"
            >
              <fluent-icon icon="add" size="16" />
              {{ t('KANBAN.AUTOMATION.ADD_AUTOMATION') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Botões -->
    <div class="flex justify-end gap-2 pt-1 px-1 pb-0.5">
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        @click="handleClose"
      >
        {{ t('KANBAN.CANCEL') }}
      </Button>
      <Button
        type="submit"
        variant="solid"
        color="blue"
        size="sm"
        :is-loading="loading"
      >
        {{ t('KANBAN.SAVE') }}
      </Button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
/* Efeito de foco nos inputs */
input,
textarea {
  @apply transition-shadow duration-200;

  &:focus {
    @apply shadow-md;
  }
}

/* Animação suave do checkbox */
input[type='checkbox'] {
  @apply transition-transform duration-200;

  &:checked {
    @apply scale-105;
  }
}

.loading-spinner {
  @apply w-4 h-4 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin;
}

input[type='date'],
input[type='datetime-local'] {
  &::-webkit-calendar-picker-indicator {
    @apply dark:invert cursor-pointer;
  }
}

input[type='checkbox'],
input[type='radio'] {
  @apply cursor-pointer;
}

select {
  @apply cursor-pointer;
}

label {
  @apply cursor-pointer;
}

/* Estilização do scroll */
::-webkit-scrollbar {
  @apply w-2;
}

::-webkit-scrollbar-track {
  @apply bg-transparent;
}

::-webkit-scrollbar-thumb {
  @apply bg-slate-300 dark:bg-slate-600 rounded-full;

  &:hover {
    @apply bg-slate-400 dark:bg-slate-500;
  }
}

/* Ajuste para o scroll */
form {
  @apply min-w-0 w-full transition-all;
}

/* Ajuste para os grids responsivos */
.grid {
  @apply min-w-0 w-full;
}

.tab-content-shell {
  @apply rounded-xl border border-slate-200 bg-white py-2.5 px-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:py-3 md:px-6;
}

.item-form-tab {
  @apply min-w-0;
}

.tab-section-header {
  @apply mb-1 flex items-center justify-between gap-3;
}

.tab-section-heading {
  @apply flex min-w-0 items-center gap-2;
}

.tab-section-icon {
  @apply flex h-7 w-7 items-center justify-center rounded-lg bg-woot-50 dark:bg-woot-900/20;
}

.tab-section-title {
  @apply text-sm font-semibold text-slate-800 dark:text-slate-100;
}

.editor-panel-shell {
  @apply flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50/40 dark:border-slate-700 dark:bg-slate-800/40;
}

.editor-panel-header {
  @apply border-b border-slate-200/70 px-3 py-2 dark:border-slate-700/70;
}

.editor-panel-body {
  @apply flex-1 p-2.5;
}

.section-card-shell {
  @apply overflow-hidden rounded-lg border border-slate-200 bg-slate-50/40 dark:border-slate-700 dark:bg-slate-800/40;
}

.section-card-body {
  @apply max-h-96 overflow-y-auto;
}

.compact-empty-state {
  @apply flex flex-col items-center justify-center py-6 text-slate-600 dark:text-slate-300;
}

.offer-selection-card {
  @apply cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:shadow-md dark:bg-slate-800;
}

.offer-preview-shell {
  @apply flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900;
}

.automation-card-shell {
  @apply rounded-lg border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-800/50;
}

/* Estilos das tabs */
.tabs-container {
  @apply mb-1;
  overflow: visible;
  min-height: 44px; /* Altura mínima para garantir visibilidade */
}

.tabs-scroll-wrapper {
  display: flex;
  flex-wrap: wrap; /* Let tabs wrap to a second row instead of horizontal scroll */
  overflow-x: visible;
  overflow-y: visible;
  scroll-behavior: smooth;
  gap: 0.25rem;
  padding: 0 0 1px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  min-height: 44px;
}

.tabs-scroll-wrapper::-webkit-scrollbar {
  height: 4px; /* Altura da scrollbar */
}

.tabs-scroll-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.tabs-scroll-wrapper::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3); /* Cor da scrollbar */
  border-radius: 2px;

  &:hover {
    background: rgba(148, 163, 184, 0.5);
  }
}

.tab-button {
  @apply relative px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 rounded-md;
  min-width: fit-content;
}

.tab-inactive:hover {
  @apply bg-slate-100 dark:bg-slate-800;
}

.tab-active {
  @apply text-woot-500;

  &::after {
    content: '';
    @apply absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500;
  }
}

.tab-inactive {
  @apply text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300;
}

/* Mobile: ajustes específicos para abas */
@media (max-width: 768px) {
  .tabs-container {
    margin-left: -0.25rem; /* Menos margem negativa em mobile */
    margin-right: -0.25rem;
    min-height: 44px;
  }

  .tabs-scroll-wrapper {
    min-height: 44px;
    padding-bottom: 4px; /* Mais espaço para scrollbar em mobile */
  }

  .tab-button {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    font-size: 0.75rem;
    line-height: 1rem;
    min-width: fit-content;
    max-width: 100px; /* Menor largura máxima em mobile */

    /* Quebrar texto se necessário */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Garantir que pelo menos parte das abas seja sempre visível */
  .tabs-scroll-wrapper {
    scroll-snap-type: x mandatory;

    .tab-button {
      scroll-snap-align: start;
    }
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .tab-button {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    font-size: 0.75rem;
    line-height: 1rem;
    min-width: 80px;
    max-width: 80px;
  }

  .tabs-container {
    min-height: 40px;
  }

  .tabs-scroll-wrapper {
    min-height: 40px;
  }
}

/* Cards com cores diferentes */
.info-card {
  @apply relative overflow-hidden flex flex-col gap-1 p-3 rounded-lg transition-all duration-200;

  &:nth-child(1) {
    @apply bg-n-iris-3 dark:bg-n-slate-8 border-n-iris-4 dark:border-n-iris-8/50;
  }

  &:nth-child(2) {
    @apply bg-n-teal-3 dark:bg-n-slate-8 border-n-teal-4 dark:border-n-teal-8/50;
  }

  &:nth-child(3) {
    @apply bg-n-amber-3 dark:bg-n-slate-8 border-n-amber-4 dark:border-n-amber-8/50;
  }

  &:nth-child(4) {
    @apply bg-n-iris-3 dark:bg-n-slate-8 border-n-iris-4 dark:border-n-iris-8/50;
  }
}
</style>
